/**
 * Admin Dispatch Pro Page — MODULE DISPATCH TEMPS RÉEL VERSION PROFESSIONNELLE
 * Phase 1 + Phase 2: D-01 to D-13, D-24, D-25
 * 
 * Layout: 4 zones
 *   1. Top: KPI Bar
 *   2. Left: Driver List (filterable)
 *   3. Center: Live Map (Google Maps)
 *   4. Right: Mission List (categorized)
 * 
 * Phase 2 additions:
 *   - D-09: Driver Profile Modal (identity, vehicle, score, compliance)
 *   - D-10: Driver missions in modal (active, future, completed)
 *   - D-11: Quick assignment from modal
 *   - D-24: Enhanced mission list blocks
 *   - D-25: Distance/ETA display
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline, TrafficLayer } from '@react-google-maps/api';
import { Link, useSearchParams } from 'react-router-dom';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import DebugOnly from '../../components/DebugOnly';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import axios from 'axios';
import ZoomableTimeline from '../../components/admin/dispatch/ZoomableTimeline';
import MissionTypeBadge from '../../components/admin/dispatch/MissionTypeBadge';
import DispatchKpiByType from '../../components/admin/dispatch/DispatchKpiByType';
import { decimatePoints } from '../../utils/gpsDecimation';
import MissionOfferModal from '../../components/admin/MissionOfferModal';
import {
  ArrowLeft, MapPin, Clock, User, Car, RefreshCw, Loader2, Radio,
  Zap, AlertTriangle, Search, CheckCircle, XCircle, Satellite,
  ChevronDown, Phone, Shield, Navigation, Users, Activity, Wifi, WifiOff,
  Mail, Star, FileCheck, ArrowRight, CornerDownRight, Timer, Route as RouteIcon,
  MessageSquare, Send, ExternalLink, Euro, Anchor, FileText, Copy, Save, Video, UserCheck, Lock
} from 'lucide-react';
import GpsVideoExportModal from '../../components/admin/GpsVideoExportModal';
import MissionAnalysisModal from '../../components/admin/MissionAnalysisModal';
import config from 'config';

const API = `${config.API_URL}/api`;
const MAPS_KEY = config.GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0a0a14" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8a8a9a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#252540" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2d2d4a" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0a3d62" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3a7ca5" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#1e1e36" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#16162a" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#1a2e1a" }] },
];

const DRIVER_STATUSES = {
  EN_MISSION: { label: 'En mission', color: '#3B82F6', bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Navigation },
  EN_LIGNE: { label: 'En ligne', color: '#10B981', bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: Wifi },
  HORS_LIGNE_RECENT: { label: 'Hors ligne récent', color: '#F59E0B', bg: 'bg-amber-500/15', text: 'text-amber-400', icon: Clock },
  DECONNECTE: { label: 'Déconnecté', color: '#6B7280', bg: 'bg-gray-500/15', text: 'text-gray-400', icon: WifiOff },
};

const MISSION_STATUS_LABELS = {
  en_route: 'En approche', on_site: 'Sur place', waiting_for_pickup: 'Attente client',
  client_on_board: 'Client a bord', dropped_off: 'Client depose', no_show: 'Client absent',
  completed: 'Termine', assigned: 'Assigne', accepted: 'Accepte',
};
const MARKER_COLORS = { EN_MISSION: '#3B82F6', EN_LIGNE: '#10B981', HORS_LIGNE_RECENT: '#F59E0B', DECONNECTE: '#6B7280' };
const STALE_COLOR = '#EF4444';

const STATUS_LABELS = {
  proposed: 'En attente chauffeur', assigned: 'Assignée', accepted: 'Acceptée', en_approche: 'En approche',
  en_attente: 'Sur place', en_attente_pec: 'Attente prise en charge', prise_en_charge: 'Client à bord', arret: 'Arrêt',
  depose_client: 'Déposé', completed: 'Terminée', cancelled: 'Annulée', unassigned: 'Non assignée',
  fleet_pending: 'En attente d\'attribution', fleet_assigned: 'Chauffeur attribué', fleet_accepted: 'Chauffeur accepté',
};
const STATUS_COLORS = {
  proposed: '#F59E0B', assigned: '#3B82F6', accepted: '#10B981', en_approche: '#F59E0B',
  en_attente: '#F97316', en_attente_pec: '#FB923C', prise_en_charge: '#06B6D4', arret: '#EAB308',
  depose_client: '#A855F7', completed: '#22C55E', unassigned: '#6B7280', cancelled: '#EF4444',
  fleet_pending: '#F59E0B', fleet_assigned: '#8B5CF6', fleet_accepted: '#10B981',
};

const formatTime = (iso) => {
  if (!iso) return '--:--';
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return '--:--'; }
};

/** Compute display status for fleet missions */
const getFleetAwareStatus = (m) => {
  if (m.target_type === 'FLEET' && m.proposal_status === 'pending') return 'fleet_pending';
  if (m.target_type === 'FLEET' && m.status === 'assigned' && m.fleet_name) return 'fleet_assigned';
  if (m.target_type === 'FLEET' && m.status === 'accepted' && m.fleet_name) return 'fleet_accepted';
  return m.status;
};
const formatStaleDuration = (seconds) => {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}min`;
  return `${Math.floor(mins / 60)}h${mins % 60}min`;
};

// ── Marker animation utilities (module-level, pure functions) ──

// T5: Haversine distance in meters between two GPS points
function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// T8: Ease-in-out for natural movement feel
function easeInOut(t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }

// T4: Interpolate heading via shortest angular path (avoids 359°→1° spinning)
function interpolateAngle(from, to, t) {
  const diff = ((to - from) + 540) % 360 - 180;
  return (from + diff * t + 360) % 360;
}

// ============== KPI BAR ==============
function KPIBar({ kpi }) {
  const items = [
    { label: 'En mission', value: kpi.en_mission, color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Navigation },
    { label: 'En ligne', value: kpi.en_ligne, color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: Wifi },
    { label: 'Hors ligne', value: kpi.hors_ligne_recent, color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Clock },
    { label: 'Déconnectés', value: kpi.deconnecte, color: 'text-gray-400', bg: 'bg-gray-500/10', icon: WifiOff },
    { label: 'Missions actives', value: kpi.active_missions, color: 'text-cyan-400', bg: 'bg-cyan-500/10', icon: Activity },
    { label: 'Non assignées', value: kpi.unassigned_missions, color: 'text-amber-400', bg: 'bg-amber-500/10', icon: AlertTriangle },
    { label: 'Terminées', value: kpi.completed_missions, color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle },
    { label: 'En retard', value: kpi.late_missions, color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle, hide: !kpi.late_missions },
  ];
  return (
    <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto scrollbar-none" data-testid="kpi-bar">
      {items.filter(i => !i.hide).map(item => {
        const Icon = item.icon;
        return (
          <div key={item.label} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${item.bg} shrink-0`}>
            <Icon className={`w-3.5 h-3.5 ${item.color}`} />
            <span className={`text-lg font-bold ${item.color}`}>{item.value}</span>
            <span className="text-[11px] text-white/50">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ============== DRIVER LIST ==============
function DriverList({ drivers, selectedDriverId, onSelectDriver, filters, onFilterChange }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    let result = drivers || [];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.full_name?.toLowerCase().includes(q) || d.phone?.includes(q) ||
        d.fleet_name?.toLowerCase().includes(q) || d.active_vehicle?.license_plate?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'ALL') result = result.filter(d => d.calculated_status === statusFilter);
    const order = { EN_MISSION: 0, EN_LIGNE: 1, HORS_LIGNE_RECENT: 2, DECONNECTE: 3 };
    result.sort((a, b) => (order[a.calculated_status] ?? 4) - (order[b.calculated_status] ?? 4));
    return result;
  }, [drivers, search, statusFilter]);

  return (
    <div className="flex flex-col h-full" data-testid="driver-list-panel">
      <div className="p-3 border-b border-white/5 space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-white/30" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher chauffeur..."
            className="pl-8 bg-white/5 border-white/10 text-white text-xs h-8" data-testid="driver-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {['ALL', 'EN_MISSION', 'EN_LIGNE', 'HORS_LIGNE_RECENT', 'DECONNECTE'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${statusFilter === s
                ? 'bg-[#C9A24D]/20 text-[#C9A24D] ring-1 ring-[#C9A24D]/30' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
              data-testid={`filter-status-${s}`}>
              {s === 'ALL' ? 'Tous' : DRIVER_STATUSES[s]?.label}
            </button>
          ))}
        </div>
        {filters?.fleets?.length > 0 && (
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1 text-[10px] text-white/30 hover:text-white/50">
            <ChevronDown className={`w-3 h-3 transition ${showFilters ? 'rotate-180' : ''}`} /> Filtres avancés
          </button>
        )}
        {showFilters && filters?.fleets?.length > 0 && (
          <div className="relative" data-testid="filter-fleet-wrapper">
            <div className="w-full rounded-xl bg-white/[0.04] backdrop-blur-sm border border-white/10 overflow-hidden">
              <button
                onClick={() => onFilterChange?.({ fleet: null })}
                className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${!filters._selectedFleet ? 'bg-[#C9A24D]/15 text-[#C9A24D]' : 'text-white/60 hover:bg-white/5'}`}
              >
                Toutes les flottes
              </button>
              {filters.fleets.map(f => (
                <button key={f.id}
                  onClick={() => onFilterChange?.({ fleet: f.id })}
                  className={`w-full text-left px-3 py-1.5 text-xs border-t border-white/5 transition-colors ${filters._selectedFleet === f.id ? 'bg-[#C9A24D]/15 text-[#C9A24D]' : 'text-white/60 hover:bg-white/5'}`}
                  data-testid={`fleet-option-${f.id}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-1" data-testid="driver-items">
        <p className="text-[10px] text-white/30 px-1 py-1">{filtered.length} chauffeur{filtered.length > 1 ? 's' : ''}</p>
        {filtered.map(driver => {
          const st = DRIVER_STATUSES[driver.calculated_status] || DRIVER_STATUSES.DECONNECTE;
          const isSelected = selectedDriverId === driver.driver_id;
          const Icon = st.icon;
          return (
            <div key={driver.driver_id} onClick={() => onSelectDriver(driver)}
              className={`p-2.5 mb-1 rounded-lg cursor-pointer transition-all border ${isSelected
                ? 'bg-[#C9A24D]/10 border-[#C9A24D]/30'
                : `${driver.is_stale && !driver.has_active_mission ? 'bg-red-500/5 border-red-500/10' : 'bg-white/[0.02] border-transparent'} hover:bg-white/5`}`}
              data-testid={`driver-item-${driver.driver_id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-white/90 truncate flex-1 mr-2">{driver.full_name}</span>
                <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${st.bg}`}>
                  <Icon className={`w-2.5 h-2.5 ${st.text}`} />
                  <span className={`text-[9px] font-medium ${st.text}`}>{st.label}</span>
                </div>
              </div>
              {driver.is_stale && driver.seconds_since_update > 120 && (
                <div className="flex items-center gap-1 mb-1">
                  <AlertTriangle className="w-2.5 h-2.5 text-red-400" />
                  <span className="text-[9px] text-red-400">Position obsolète ({formatStaleDuration(driver.seconds_since_update)})</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-[10px] text-white/40">
                {driver.active_mission_status && (
                  <span className={`flex items-center gap-0.5 font-semibold ${
                    driver.active_mission_status === 'en_route' ? 'text-blue-400' :
                    driver.active_mission_status === 'waiting_for_pickup' ? 'text-amber-400' :
                    driver.active_mission_status === 'client_on_board' ? 'text-cyan-400' :
                    driver.active_mission_status === 'dropped_off' ? 'text-purple-400' :
                    driver.active_mission_status === 'no_show' ? 'text-red-400' :
                    'text-blue-300'
                  }`}>
                    {driver.active_mission_status === 'en_route' ? 'En approche' :
                     driver.active_mission_status === 'on_site' ? 'Sur place' :
                     driver.active_mission_status === 'waiting_for_pickup' ? 'Attente client' :
                     driver.active_mission_status === 'client_on_board' ? 'Client a bord' :
                     driver.active_mission_status === 'dropped_off' ? 'Depose' :
                     driver.active_mission_status === 'no_show' ? 'No-show' :
                     driver.active_mission_status}
                  </span>
                )}
                {driver.fleet_name && <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{driver.fleet_name}</span>}
                {driver.active_vehicle && <span className="flex items-center gap-0.5"><Car className="w-2.5 h-2.5" />{driver.active_vehicle.license_plate}{driver.active_vehicle.color ? ` · ${driver.active_vehicle.color}` : ''}</span>}
                <span className="flex items-center gap-0.5"><Activity className="w-2.5 h-2.5" />{driver.missions_today}m</span>
                {/* T9: GPS connection indicator */}
                {driver.gps_connection && driver.calculated_status !== 'DECONNECTE' && (
                  <span className={`flex items-center gap-0.5 font-medium ${
                    driver.gps_connection === 'OK' ? 'text-emerald-400' :
                    driver.gps_connection === 'LAGGING' ? 'text-amber-400' :
                    driver.gps_connection === 'LOST' ? 'text-red-400' : 'text-white/30'
                  }`}>
                    <Satellite className="w-2.5 h-2.5" />
                    {driver.gps_connection === 'OK' ? 'GPS' :
                     driver.gps_connection === 'LAGGING' ? 'GPS!' :
                     driver.gps_connection === 'LOST' ? 'GPS✗' : '?'}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-xs text-white/30 text-center py-8">Aucun chauffeur trouvé</p>}
      </div>
    </div>
  );
}

// ============== D-24: ENHANCED MISSION LIST ==============
function MissionList({ missions, onSelectMission, selectedDriverId, onDateChange, onDateEndChange, onTypeChange, currentDate, currentDateEnd }) {
  const [activeTab, setActiveTab] = useState('active');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const tabs = [
    { key: 'active', label: 'Actives', data: missions?.active, color: 'text-blue-400' },
    { key: 'unassigned', label: 'Non assignées', data: missions?.unassigned, color: 'text-amber-400' },
    { key: 'late', label: 'En retard', data: missions?.late, color: 'text-red-400' },
    { key: 'future', label: 'Futures', data: missions?.future, color: 'text-cyan-400' },
    { key: 'completed', label: 'Terminées', data: missions?.completed, color: 'text-green-400' },
  ];
  const currentData = tabs.find(t => t.key === activeTab)?.data || [];

  // Helpers for the quick-date presets — keep Paris-local YYYY-MM-DD format.
  const localISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === 'today') {
      const t = localISO(now);
      onDateChange?.(t);
      onDateEndChange?.(t);
    } else if (preset === 'tomorrow') {
      const t = new Date(now); t.setDate(t.getDate() + 1);
      const s = localISO(t);
      onDateChange?.(s);
      onDateEndChange?.(s);
    } else if (preset === 'week') {
      const start = new Date(now);
      const dow = (start.getDay() + 6) % 7; // Monday = 0
      start.setDate(start.getDate() - dow);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      onDateChange?.(localISO(start));
      onDateEndChange?.(localISO(end));
    } else if (preset === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      onDateChange?.(localISO(start));
      onDateEndChange?.(localISO(end));
    } else if (preset === 'reset') {
      onDateChange?.(localISO(now));
      onDateEndChange?.('');
    }
  };
  // Detect which preset is currently active to highlight the corresponding button.
  const activePreset = (() => {
    if (!currentDate) return null;
    const now = new Date();
    const today = localISO(now);
    const tom = new Date(now); tom.setDate(tom.getDate() + 1);
    const tomorrow = localISO(tom);
    const monday = new Date(now);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(sunday.getDate() + 6);
    const monthStart = localISO(new Date(now.getFullYear(), now.getMonth(), 1));
    const monthEnd = localISO(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    if (currentDate === today && currentDateEnd === today) return 'today';
    if (currentDate === today && !currentDateEnd) return 'reset';
    if (currentDate === tomorrow && currentDateEnd === tomorrow) return 'tomorrow';
    if (currentDate === localISO(monday) && currentDateEnd === localISO(sunday)) return 'week';
    if (currentDate === monthStart && currentDateEnd === monthEnd) return 'month';
    return null;
  })();
  const presets = [
    { key: 'today', label: "Aujourd'hui" },
    { key: 'tomorrow', label: 'Demain' },
    { key: 'week', label: 'Semaine' },
    { key: 'month', label: 'Mois' },
    { key: 'reset', label: 'À partir d\'aujourd\'hui' },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="mission-list-panel">
      {/* Date range filter + Type filter */}
      <div className="p-2 border-b border-white/5 space-y-2">
        {/* Quick presets */}
        <div className="flex flex-wrap gap-1" data-testid="mission-date-presets">
          {presets.map(p => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                activePreset === p.key
                  ? 'bg-[#C9A24D]/20 text-[#C9A24D] ring-1 ring-[#C9A24D]/30'
                  : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/60'
              }`}
              data-testid={`mission-date-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <div className="flex-1">
            <label className="text-[8px] text-white/30 uppercase">Du</label>
            <input type="date" value={currentDate || ''} onChange={e => onDateChange?.(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[11px] text-white"
              data-testid="mission-date-filter" />
          </div>
          <div className="flex-1">
            <label className="text-[8px] text-white/30 uppercase">Au</label>
            <input type="date" value={currentDateEnd || ''} onChange={e => onDateEndChange?.(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[11px] text-white"
              placeholder="Sans fin"
              data-testid="mission-date-end-filter" />
          </div>
        </div>
        <div className="flex gap-1">
          {['ALL', 'VTC', 'EXCURSION', 'MARITIME'].map(t => (
            <button key={t} onClick={() => { setTypeFilter(t); onTypeChange?.(t === 'ALL' ? null : t); }}
              className={`flex-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-all ${typeFilter === t
                ? 'bg-[#C9A24D]/20 text-[#C9A24D] ring-1 ring-[#C9A24D]/30'
                : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
              data-testid={`type-filter-${t}`}>
              {t === 'ALL' ? 'Tous' : t === 'VTC' ? 'Transferts' : t === 'EXCURSION' ? 'Excursions' : 'Maritimes'}
            </button>
          ))}
        </div>
      </div>
      <div className="flex border-b border-white/5 overflow-x-auto scrollbar-none">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1 px-3 py-2 text-[10px] font-medium whitespace-nowrap border-b-2 transition ${activeTab === tab.key
              ? `${tab.color} border-current` : 'text-white/30 border-transparent hover:text-white/50'}`}
            data-testid={`mission-tab-${tab.key}`}>
            {tab.label}
            {(tab.data?.length || 0) > 0 && (
              <span className={`ml-1 px-1.5 py-0 rounded-full text-[9px] ${activeTab === tab.key ? 'bg-white/10' : 'bg-white/5'}`}>{tab.data.length}</span>
            )}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-1" data-testid="mission-items">
        {currentData.length === 0 ? (
          <p className="text-xs text-white/30 text-center py-8">Aucune mission</p>
        ) : (() => {
          // Group round-trip missions: show outbound with return nested, hide standalone return entries
          const returnIds = new Set();
          currentData.forEach(m => {
            if (m.round_trip_type === 'outbound' && m.linked_mission_id) {
              returnIds.add(m.linked_mission_id);
            }
          });

          // Group shared excursion missions (navettes) by shared_trip_id
          const shuttleGroups = {};
          const shuttleMemberIds = new Set();
          currentData.forEach(m => {
            if (m.type === 'EXCURSION' && m.shared_trip_id && m.is_shared) {
              if (!shuttleGroups[m.shared_trip_id]) {
                shuttleGroups[m.shared_trip_id] = [];
              }
              shuttleGroups[m.shared_trip_id].push(m);
              shuttleMemberIds.add(m.id);
            }
          });

          // Build unified render items with sort key
          const items = [];

          currentData
            .filter(m => !returnIds.has(m.id) && !shuttleMemberIds.has(m.id))
            .forEach(m => {
              const linkedReturn = m.round_trip_type === 'outbound' && m.linked_mission_id
                ? currentData.find(r => r.id === m.linked_mission_id)
                : null;
              items.push({
                sortKey: m.start_time || '',
                node: <MissionCard key={m.id} mission={m} linkedReturn={linkedReturn} onSelect={onSelectMission} selectedDriverId={selectedDriverId} />,
              });
            });

          Object.entries(shuttleGroups).forEach(([tripId, members]) => {
            items.push({
              sortKey: members[0]?.start_time || '',
              node: <ShuttleGroupCard key={`shuttle-${tripId}`} tripId={tripId} members={members} onSelect={onSelectMission} selectedDriverId={selectedDriverId} />,
            });
          });

          // Sort by start_time for natural chronological order
          items.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
          return items.map(i => i.node);
        })()}
      </div>
    </div>
  );
}

// Shuttle Group Card: groups shared excursion missions into a single collapsible "Navette" card
// Clicking the card opens the detail/assignment modal; chevron expands member list
function ShuttleGroupCard({ tripId, members, onSelect, selectedDriverId }) {
  const [expanded, setExpanded] = useState(false);

  const totalPax = members.reduce((sum, m) => sum + (m.passenger_count || 0), 0);
  const firstMission = members[0];
  const experienceName = firstMission?.experience_name || 'Navette';
  const startTime = firstMission?.start_time;
  const driverName = members.find(m => m.driver_name)?.driver_name || '';
  const driverId = members.find(m => m.assigned_driver_id)?.assigned_driver_id;
  const pickupAddress = firstMission?.pickup_address || '';

  // Determine aggregate status
  const statuses = members.map(m => m.status);
  const aggregateStatus = statuses.every(s => s === 'completed') ? 'completed'
    : statuses.some(s => s === 'prise_en_charge' || s === 'en_approche' || s === 'en_attente' || s === 'en_attente_pec') ? 'en_approche'
    : driverId ? 'assigned' : 'unassigned';

  // Build a representative shuttle mission object for the detail modal
  const handleShuttleClick = () => {
    // Compute shuttle fee from shared trip data or member missions
    const firstFee = firstMission?.proposed_fee || firstMission?.driver_fee || 0;
    const shuttleTotalFee = firstFee * members.length; // per-resa × count = total
    const shuttleMission = {
      ...firstMission,
      _isShuttle: true,
      _shuttleTripId: tripId,
      _shuttleMembers: members,
      _shuttleTotalPax: totalPax,
      _shuttleDriverName: driverName,
      _shuttleFee: shuttleTotalFee,
      proposed_fee: shuttleTotalFee,
      passenger_count: totalPax,
      client_name: `Navette · ${members.length} résa · ${totalPax} pax`,
    };
    onSelect?.(shuttleMission);
  };

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <div className="mb-1 rounded-lg border border-purple-500/20 bg-purple-500/[0.04] overflow-hidden"
      data-testid={`shuttle-group-${tripId}`}>
      {/* Header — click opens detail/assignment modal */}
      <div className="p-2.5 cursor-pointer hover:bg-purple-500/[0.08] transition"
        onClick={handleShuttleClick}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <Anchor className="w-3.5 h-3.5 text-purple-400" />
            <span className="text-[10px] font-bold text-purple-400 uppercase">Navette</span>
            <Badge className="text-[9px] bg-purple-500/15 text-purple-300 border-0">{members.length} résa</Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[aggregateStatus] || '#6B7280' }} />
            <span className="text-[10px]" style={{ color: STATUS_COLORS[aggregateStatus] }}>{STATUS_LABELS[aggregateStatus] || aggregateStatus}</span>
            <span className="text-[10px] text-white/30">{formatTime(startTime)}</span>
            <button onClick={handleToggleExpand}
              className="p-0.5 rounded hover:bg-white/10 transition" data-testid={`shuttle-expand-${tripId}`}>
              <ChevronDown className={`w-3 h-3 text-white/30 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
        <p className="text-xs text-white/70 truncate">{experienceName}</p>
        {pickupAddress && <p className="text-[10px] text-white/30 truncate mt-0.5"><MapPin className="w-2.5 h-2.5 inline mr-0.5" />{pickupAddress}</p>}
        <div className="flex items-center justify-between mt-1 text-[10px] text-white/30">
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />{totalPax} pax
          </span>
          {driverName ? (
            <span className="text-blue-400/70">{driverName}</span>
          ) : (
            <span className="text-amber-400/70">Non assignée</span>
          )}
        </div>
      </div>
      {/* Expanded: individual member list (informational) */}
      {expanded && (
        <div className="border-t border-purple-500/15 px-2 py-1 space-y-0.5" data-testid={`shuttle-members-${tripId}`}>
          {members.map(m => (
            <div key={m.id}
              className="p-2 rounded-md bg-white/[0.02]"
              data-testid={`shuttle-member-${m.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[m.status] || '#6B7280' }} />
                  <span className="text-[10px] text-white/60">{m.client_name || 'N/A'}</span>
                </div>
                <span className="text-[10px] text-white/30">{m.passenger_count || 0} pax</span>
              </div>
              {m.pickup_address && <p className="text-[9px] text-white/25 truncate mt-0.5 pl-3">{m.pickup_address}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// D-25: Mission card with distance/ETA + round-trip grouping
function MissionCard({ mission: m, linkedReturn, onSelect, selectedDriverId }) {
  const [distInfo, setDistInfo] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const displayStatus = getFleetAwareStatus(m);

  // D-25: Fetch distance when driver selected and mission has pickup coords
  useEffect(() => {
    if (!selectedDriverId || !m.pickup_lat || !m.pickup_lng) { setDistInfo(null); return; }
    const fetchDist = async () => {
      try {
        const { data } = await axios.get(
          `${API}/dispatch/pro/distance?driver_id=${selectedDriverId}&pickup_lat=${m.pickup_lat}&pickup_lng=${m.pickup_lng}`,
          { headers: getHeaders() }
        );
        setDistInfo(data);
      } catch { setDistInfo(null); }
    };
    fetchDist();
  }, [selectedDriverId, m.pickup_lat, m.pickup_lng]);

  const handleQuickTrackingLink = async (e) => {
    e.stopPropagation();
    try {
      const mType = m.type === 'EXCURSION' ? 'EXCURSION' : 'VTC';
      const { data: res } = await axios.post(
        `${API}/dispatch/pro/tracking-links/generate/${mType}/${m.id}`,
        {}, { headers: getHeaders() }
      );
      if (res.data?.public_token) {
        const url = `${window.location.origin}/track/${res.data.public_token}`;
        navigator.clipboard.writeText(url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    } catch { /* silent */ }
  };

  return (
    <div onClick={() => onSelect?.(m)}
      className="p-2.5 mb-1 rounded-lg bg-white/[0.02] border border-transparent hover:bg-white/5 hover:border-white/5 cursor-pointer transition"
      data-testid={`mission-item-${m.id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[displayStatus] || '#6B7280' }} />
          <span className="text-[10px] font-medium" style={{ color: STATUS_COLORS[displayStatus] }}>{STATUS_LABELS[displayStatus]}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <MissionTypeBadge mission={m} size="xs" />
          {(m.linked_mission_id || m.is_round_trip) && <Badge className="text-[8px] bg-cyan-500/15 text-cyan-400 border-0">A/R</Badge>}
          <span className="text-[10px] text-white/30">{formatTime(m.start_time)}</span>
        </div>
      </div>
      <p className="text-xs text-white/70 truncate">{m.pickup_address || m.ref_id || 'Adresse non renseignée'}</p>
      {m.dropoff_address && <p className="text-[10px] text-white/30 truncate mt-0.5"><CornerDownRight className="w-2.5 h-2.5 inline mr-0.5" />{m.dropoff_address}</p>}
      <div className="flex items-center justify-between mt-1 text-[10px] text-white/30">
        <span>{m.client_name || 'Client N/A'}{m.passenger_count > 0 ? ` · ${m.passenger_count} pax` : ''}</span>
        {displayStatus === 'fleet_pending' ? (
          <span className="text-amber-400/70">Flotte: {m.fleet_name || m.proposed_partner_name || '—'}</span>
        ) : displayStatus === 'fleet_assigned' ? (
          <span className="text-purple-400/70">{m.driver_name} · {m.fleet_name}</span>
        ) : displayStatus === 'fleet_accepted' ? (
          <span className="text-emerald-400/70">{m.driver_name} · {m.fleet_name}</span>
        ) : m.status === 'proposed' ? (
          <span className="text-amber-400/70">Proposée → {m.proposed_driver_name}</span>
        ) : m.assigned_driver_id ? (
          <span className="text-blue-400/70">Assignée</span>
        ) : (
          <span className="text-amber-400/70">Non assignée</span>
        )}
      </div>
      {/* D-25: Distance/ETA */}
      {distInfo && distInfo.distance_km != null && (
        <div className="flex items-center gap-2 mt-1.5 text-[10px]" data-testid={`distance-${m.id}`}>
          <span className="flex items-center gap-0.5 text-cyan-400"><RouteIcon className="w-2.5 h-2.5" />{distInfo.distance_km} km</span>
          <span className="flex items-center gap-0.5 text-amber-400"><Timer className="w-2.5 h-2.5" />~{distInfo.eta_minutes} min</span>
        </div>
      )}
      {/* Quick tracking link */}
      {m.assigned_driver_id && (
        <button onClick={handleQuickTrackingLink}
          className={`mt-1.5 flex items-center gap-1 text-[9px] px-2 py-0.5 rounded transition-colors ${
            linkCopied ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/5 text-white/30 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10'
          }`}
          data-testid={`quick-tracking-${m.id}`}>
          <Radio className="w-2.5 h-2.5" />
          {linkCopied ? 'Lien copié !' : 'Lien de suivi'}
        </button>
      )}
      {/* Round-trip: grouped return mission */}
      {linkedReturn && (
        <div className="mt-2 pt-2 border-t border-cyan-500/20"
          onClick={(e) => { e.stopPropagation(); onSelect?.(linkedReturn); }}>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[9px] font-bold text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">RETOUR</span>
            <span className="text-[10px] text-white/30">{formatTime(linkedReturn.start_time)}</span>
            <span className="text-[10px] text-white/20">{linkedReturn.booking_number || linkedReturn.ref_id || ''}</span>
          </div>
          <p className="text-[10px] text-cyan-300/60 truncate">{linkedReturn.pickup_address || 'Adresse retour'}</p>
          {linkedReturn.dropoff_address && <p className="text-[9px] text-white/20 truncate"><CornerDownRight className="w-2.5 h-2.5 inline mr-0.5" />{linkedReturn.dropoff_address}</p>}
        </div>
      )}
    </div>
  );
}

// ============== D-09: DRIVER PROFILE MODAL ==============
function DriverProfileModal({ open, onClose, driverId, unassignedMissions, onAssign, fetchSnapshot, onOpenMissionDetail, initialTab, initialDate }) {
  const [profile, setProfile] = useState(null);
  const [driverMissions, setDriverMissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab || 'profile');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [selectedMissionForAssign, setSelectedMissionForAssign] = useState(null);
  // Phase 3 state
  const todayStr = new Date().toISOString().slice(0, 10);
  const [historyDate, setHistoryDate] = useState(initialDate || todayStr);
  const [historyDateEnd, setHistoryDateEnd] = useState(initialDate || todayStr);  // D2-13
  const [locationHistory, setLocationHistory] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [missionEvents, setMissionEvents] = useState([]);  // TL-21
  const [anomalies, setAnomalies] = useState([]);  // Anomaly detection
  const [pointsWarning, setPointsWarning] = useState(null);  // TL-24
  // Trace map state (embedded map in modal)
  const [tracePosition, setTracePosition] = useState(null);
  const [modalTraffic, setModalTraffic] = useState(false);  // TL-28
  const [satelliteMode, setSatelliteMode] = useState(false);  // Auto satellite at 15min zoom
  const traceMapRef = useRef(null);
  const historyCacheRef = useRef({});  // TL-26: cache
  // Phase 4 chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatConvId, setChatConvId] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const chatEndRef = useRef(null);

  // T11: Decimated polyline path for map performance
  const decimatedPath = useMemo(() => {
    const pts = locationHistory?.points;
    if (!pts || pts.length <= 2) return pts || [];
    return decimatePoints(pts, 500);
  }, [locationHistory?.points]);

  // Snap-to-road interpolated path for road-following polyline
  // ZERO API CALLS: Use server-side snapped data or local Catmull-Rom smoothing only
  const [snappedHistoryPath, setSnappedHistoryPath] = useState(null);

  useEffect(() => {
    if (!decimatedPath || decimatedPath.length < 2) {
      setSnappedHistoryPath(null);
      return;
    }

    // Priority 1: Use server-side snapped coordinates if available
    const hasServerSnap = decimatedPath.some(p => p.lat_snapped && p.lng_snapped);
    if (hasServerSnap) {
      const serverSnapped = decimatedPath.map(p => ({
        lat: p.lat_snapped || p.lat,
        lng: p.lng_snapped || p.lng,
      }));
      setSnappedHistoryPath(serverSnapped);
      return;
    }

    // Priority 2: Local Catmull-Rom smoothing (zero API cost)
    try {
      // Filter points — remove close + reject huge jumps (outliers)
      const MAX_JUMP_M = 1000; // 1km max between consecutive points
      const filtered = [];
      let lastPt = null;
      for (const p of decimatedPath) {
        if (p.accuracy && p.accuracy > 20) continue;
        if (lastPt) {
          const dlat = Math.abs(p.lat - lastPt.lat);
          const dlng = Math.abs(p.lng - lastPt.lng);
          const distM = Math.sqrt(dlat * dlat + dlng * dlng) * 111000;
          if (distM < 10) continue;       // too close
          if (distM > MAX_JUMP_M) continue; // outlier jump — skip
        }
        filtered.push({ lat: p.lat, lng: p.lng });
        lastPt = p;
      }
      if (filtered.length < 2) { setSnappedHistoryPath(null); return; }

      // Interpolate + smooth locally
      const interpolated = [];
      for (let i = 0; i < filtered.length - 1; i++) {
        const a = filtered[i], b = filtered[i + 1];
        const dist = Math.sqrt(Math.pow(b.lat - a.lat, 2) + Math.pow(b.lng - a.lng, 2)) * 111000;
        if (dist > 500) {
          // Large gap: add straight without interpolation
          interpolated.push(a);
        } else if (dist > 15) {
          const n = Math.ceil(dist / 15);
          for (let j = 0; j < n; j++) {
            const t = j / n;
            interpolated.push({ lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t });
          }
        } else {
          interpolated.push(a);
        }
      }
      interpolated.push(filtered[filtered.length - 1]);

      // Catmull-Rom spline — skip large gaps
      if (interpolated.length >= 3) {
        const smooth = [];
        const tension = 0.5, segs = 6;
        for (let i = 0; i < interpolated.length - 1; i++) {
          const p1 = interpolated[i];
          const p2 = interpolated[i + 1];
          // Skip spline on large gaps (just connect straight)
          const gapDist = Math.sqrt(Math.pow(p2.lat - p1.lat, 2) + Math.pow(p2.lng - p1.lng, 2)) * 111000;
          if (gapDist > 500) { smooth.push(p1); continue; }
          const p0 = interpolated[Math.max(0, i - 1)];
          const p3 = interpolated[Math.min(interpolated.length - 1, i + 2)];
          smooth.push(p1);
          for (let j = 1; j < segs; j++) {
            const t = j / segs, t2 = t * t, t3 = t2 * t;
            const s = (1 - tension) / 2;
            smooth.push({
              lat: s * ((-t3 + 2 * t2 - t) * p0.lat + (3 * t3 - 5 * t2 + 2) * p1.lat + (-3 * t3 + 4 * t2 + t) * p2.lat + (t3 - t2) * p3.lat),
              lng: s * ((-t3 + 2 * t2 - t) * p0.lng + (3 * t3 - 5 * t2 + 2) * p1.lng + (-3 * t3 + 4 * t2 + t) * p2.lng + (t3 - t2) * p3.lng),
            });
          }
        }
        smooth.push(interpolated[interpolated.length - 1]);
        setSnappedHistoryPath(smooth);
      } else {
        setSnappedHistoryPath(interpolated);
      }
    } catch {
      setSnappedHistoryPath(null);
    }
  }, [decimatedPath]);


  useEffect(() => {
    if (!open || !driverId) return;
    setLoading(true);
    setActiveTab(initialTab || 'profile');
    setValidationResult(null);
    setSelectedMissionForAssign(null);
    // Apply initialDate when opening the modal
    if (initialDate) {
      setHistoryDate(initialDate);
      setHistoryDateEnd(initialDate);
    }
    Promise.all([
      axios.get(`${API}/dispatch/pro/driver/${driverId}/profile`, { headers: getHeaders() }).then(r => setProfile(r.data)),
      axios.get(`${API}/dispatch/pro/driver/${driverId}/missions`, { headers: getHeaders() }).then(r => setDriverMissions(r.data)),
    ]).catch(err => console.error('Profile load error', err)).finally(() => setLoading(false));
  }, [open, driverId, initialTab, initialDate]);

  // Phase 3 + TL-24/TL-26: Fetch GPS history + timeline + events + anomalies (with cache)
  const fetchHistory = useCallback(async (dateStr) => {
    if (!driverId) return;
    const cacheKey = `${driverId}_${dateStr}_${historyDateEnd}`;

    // TL-26: Check cache first
    if (historyCacheRef.current[cacheKey]) {
      const cached = historyCacheRef.current[cacheKey];
      setLocationHistory(cached.location);
      setTimeline(cached.timeline);
      setMissionEvents(cached.events);
      setAnomalies(cached.anomalies || []);
      setPointsWarning(cached.location?.total_points > 5000 ? cached.location.total_points : null);
      return;
    }

    setHistoryLoading(true);
    setTracePosition(null);
    setPointsWarning(null);
    setAnomalies([]);
    setSnappedHistoryPath(null);
    try {
      const isSameDay = historyDateEnd === dateStr;
      const locUrl = isSameDay
        ? `${API}/dispatch/pro/driver/${driverId}/locations?date=${dateStr}`
        : `${API}/dispatch/pro/driver/${driverId}/locations-range?date_start=${dateStr}&date_end=${historyDateEnd}`;
      const [locRes, tlRes, evtRes, anomalyRes] = await Promise.all([
        axios.get(locUrl, { headers: getHeaders() }),
        axios.get(`${API}/dispatch/pro/driver/${driverId}/timeline?date=${dateStr}`, { headers: getHeaders() }),
        axios.get(`${API}/dispatch/pro/driver/${driverId}/events-range?start=${dateStr}&end=${historyDateEnd}`, { headers: getHeaders() }).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/dispatch/pro/driver/${driverId}/anomalies?date=${dateStr}`, { headers: getHeaders() }).catch(() => ({ data: { anomalies: [] } })),
      ]);
      setLocationHistory(locRes.data);
      setTimeline(tlRes.data);
      setMissionEvents(evtRes.data?.events || []);
      setAnomalies(anomalyRes.data?.anomalies || []);

      // TL-24: Warn if too many points
      if (locRes.data?.total_points > 5000) setPointsWarning(locRes.data.total_points);

      // TL-26: Store in cache (T12: limit cache size to 10 entries)
      const cache = historyCacheRef.current;
      cache[cacheKey] = {
        location: locRes.data,
        timeline: tlRes.data,
        events: evtRes.data?.events || [],
        anomalies: anomalyRes.data?.anomalies || [],
      };
      const keys = Object.keys(cache);
      if (keys.length > 10) {
        delete cache[keys[0]];
      }
    } catch (err) { console.error('History error', err); }
    setHistoryLoading(false);
  }, [driverId, historyDateEnd]);

  // Load history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history' && driverId) fetchHistory(historyDate);
  }, [activeTab, historyDate, driverId, fetchHistory]);

  // Phase 4: Fetch chat messages
  const fetchChat = useCallback(async () => {
    if (!driverId) return;
    setChatLoading(true);
    try {
      const { data } = await axios.get(`${API}/dispatch/pro/conversations/${driverId}/messages`, { headers: getHeaders() });
      setChatMessages(data.messages || []);
      setChatConvId(data.conversation_id);
      setChatUnread(data.unread_admin || 0);
      // Mark as read
      if (data.unread_admin > 0) {
        await axios.post(`${API}/dispatch/pro/conversations/${driverId}/read`, {}, { headers: getHeaders() });
        setChatUnread(0);
      }
    } catch (err) { console.error('Chat load error', err); }
    setChatLoading(false);
  }, [driverId]);

  useEffect(() => {
    if (activeTab === 'chat' && driverId) fetchChat();
  }, [activeTab, driverId, fetchChat]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Send message
  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatSending) return;
    setChatSending(true);
    try {
      const { data } = await axios.post(
        `${API}/dispatch/pro/conversations/${driverId}/messages`,
        { content: chatInput.trim() }, { headers: getHeaders() }
      );
      setChatMessages(prev => [...prev, data]);
      setChatInput('');
    } catch (err) { console.error('Send error', err); }
    setChatSending(false);
  };

  // D-11: Quick assignment validation
  const handleValidate = async (missionId, missionSource) => {
    setValidating(true);
    setValidationResult(null);
    setSelectedMissionForAssign({ id: missionId, source: missionSource });
    try {
      const { data } = await axios.post(
        `${API}/dispatch/pro/validate-assignment?driver_id=${driverId}&mission_id=${missionId}&mission_source=${missionSource}`,
        {}, { headers: getHeaders() }
      );
      setValidationResult(data);
    } catch (err) {
      setValidationResult({ valid: false, errors: [{ code: 'ERROR', message: err.response?.data?.detail || 'Erreur' }] });
    }
    setValidating(false);
  };

  // D-11: Execute assignment
  const handleAssignMission = async () => {
    if (!selectedMissionForAssign) return;
    setAssigning(true);
    try {
      if (selectedMissionForAssign.source === 'dispatch_tasks') {
        const { data: task } = await axios.get(`${API}/dispatch/tasks/${selectedMissionForAssign.id}`, { headers: getHeaders() });
        await axios.post(`${API}/dispatch/assign`, {
          task_id: selectedMissionForAssign.id, driver_id: driverId, lock_version: task.lock_version
        }, { headers: getHeaders() });
      }
      await fetchSnapshot?.();
      setSelectedMissionForAssign(null);
      setValidationResult(null);
      // Refresh driver missions
      const { data: m } = await axios.get(`${API}/dispatch/pro/driver/${driverId}/missions`, { headers: getHeaders() });
      setDriverMissions(m);
    } catch (err) {
      alert(err.response?.data?.detail?.message || err.response?.data?.detail || 'Erreur d\'affectation');
    }
    setAssigning(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o) {
        // T12: Map memory cleanup on modal close
        traceMapRef.current = null;
        setLocationHistory(null);
        setTimeline(null);
        setMissionEvents([]);
        setAnomalies([]);
        setTracePosition(null);
        onClose();
      }
    }}>
      <DialogContent className="bg-[#0e0e1a] border-white/10 text-white w-[98vw] md:max-w-2xl max-h-[95vh] md:max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="driver-profile-modal">
        <DialogHeader className="shrink-0 px-4 pt-4 pb-2 md:px-6 md:pt-6">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-[#C9A24D]" />
            {loading ? 'Chargement...' : profile?.identity?.full_name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C9A24D]" /></div>
        ) : (
          <>
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="bg-white/5 border-b border-white/5 shrink-0 overflow-x-auto flex w-full justify-start md:justify-center gap-0 scrollbar-hide">
                <TabsTrigger value="profile" className="text-[10px] md:text-xs whitespace-nowrap data-[state=active]:bg-[#C9A24D]/20 data-[state=active]:text-[#C9A24D]" data-testid="tab-profile">Profil</TabsTrigger>
                <TabsTrigger value="missions" className="text-[10px] md:text-xs whitespace-nowrap data-[state=active]:bg-[#C9A24D]/20 data-[state=active]:text-[#C9A24D]" data-testid="tab-missions">
                  Missions {driverMissions?.total > 0 && <Badge className="ml-1 text-[9px] bg-white/10 border-0">{driverMissions.total}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="assign" className="text-[10px] md:text-xs whitespace-nowrap data-[state=active]:bg-[#C9A24D]/20 data-[state=active]:text-[#C9A24D]" data-testid="tab-assign">
                  Affecter {(() => {
                    const count = (unassignedMissions || []).filter(m => !m.proposed_driver_id || m.proposed_driver_id === driverId).length;
                    return count > 0 ? <Badge className="ml-1 text-[9px] bg-amber-500/20 text-amber-400 border-0">{count}</Badge> : null;
                  })()}
                </TabsTrigger>
                <TabsTrigger value="history" className="text-[10px] md:text-xs whitespace-nowrap data-[state=active]:bg-[#C9A24D]/20 data-[state=active]:text-[#C9A24D]" data-testid="tab-history">
                  Historique
                </TabsTrigger>
                <TabsTrigger value="chat" className="text-[10px] md:text-xs whitespace-nowrap data-[state=active]:bg-[#C9A24D]/20 data-[state=active]:text-[#C9A24D]" data-testid="tab-chat">
                  <MessageSquare className="w-3 h-3 mr-1" />Chat
                  {chatUnread > 0 && <Badge className="ml-1 text-[9px] bg-red-500/20 text-red-400 border-0">{chatUnread}</Badge>}
                </TabsTrigger>
              </TabsList>

              {/* D-09: Profile Tab */}
              <TabsContent value="profile" className="flex-1 overflow-y-auto px-3 py-3 md:p-4 space-y-3 md:space-y-4 mt-0">
                {/* Identity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3" data-testid="profile-identity">
                  <InfoField icon={User} label="Nom" value={profile?.identity?.full_name} />
                  <InfoField icon={Phone} label="Téléphone" value={profile?.identity?.phone} />
                  <InfoField icon={Mail} label="Email" value={profile?.identity?.email} />
                  <InfoField icon={MapPin} label="Ville" value={profile?.identity?.city || 'Non renseignée'} />
                  <InfoField icon={Star} label="Score" value={`${profile?.score?.driver_score}/100`} />
                  <InfoField icon={Activity} label="Missions total" value={profile?.score?.total_missions} />
                </div>

                {/* Status */}
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <h4 className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Statut</h4>
                  <div className="flex items-center gap-3">
                    <Badge className={`text-xs ${profile?.identity?.driver_status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                      {profile?.identity?.driver_status}
                    </Badge>
                    {profile?.fleet && <span className="text-xs text-white/50">Flotte: {profile.fleet.name}</span>}
                  </div>
                  {/* Score details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-[10px]">
                    <div className="text-center p-1.5 rounded bg-white/[0.03]"><p className="text-emerald-400 font-bold">{profile?.score?.completed_missions}</p><p className="text-white/30">Terminées</p></div>
                    <div className="text-center p-1.5 rounded bg-white/[0.03]"><p className="text-red-400 font-bold">{profile?.score?.cancelled_missions}</p><p className="text-white/30">Annulées</p></div>
                    <div className="text-center p-1.5 rounded bg-white/[0.03]"><p className="text-amber-400 font-bold">{profile?.score?.refusal_count}</p><p className="text-white/30">Refus</p></div>
                    <div className="text-center p-1.5 rounded bg-white/[0.03]"><p className="text-orange-400 font-bold">{profile?.score?.penalty_count}</p><p className="text-white/30">Pénalités</p></div>
                  </div>
                </div>

                {/* Vehicles - Grouped by approval status */}
                <div data-testid="profile-vehicles">
                  <h4 className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Véhicule(s)</h4>
                  {profile?.vehicles?.length > 0 ? (() => {
                    const approved = profile.vehicles.filter(v => v.status === 'APPROVED');
                    const notApproved = profile.vehicles.filter(v => v.status !== 'APPROVED');
                    return (
                      <>
                        {approved.length > 0 && (
                          <div className="mb-3">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <CheckCircle className="w-3 h-3 text-emerald-400" />
                              <span className="text-[10px] text-emerald-400 font-medium uppercase tracking-wider">Approuvés ({approved.length})</span>
                            </div>
                            {approved.map(v => (
                              <div key={v.id} className="p-3 rounded-lg bg-emerald-500/[0.05] border border-emerald-500/20 mb-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium">{v.brand} {v.model}</span>
                                  <div className="flex items-center gap-1.5">
                                    <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400">{v.status}</Badge>
                                    <a href={`/admin/vehicules?id=${v.id}`} className="text-[9px] text-[#C9A24D] hover:text-[#E6C77A] hover:underline flex items-center gap-0.5" title="Gérer ce véhicule" data-testid={`vehicle-link-${v.id}`}>
                                      <ExternalLink className="w-3 h-3" />Gérer
                                    </a>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 text-[10px] text-white/40">
                                  <span><Car className="w-3 h-3 inline mr-0.5" />{v.license_plate}</span>
                                  <span>{v.vehicle_type}</span>
                                  {v.color && <span>{v.color}</span>}
                                </div>
                                {(v.ct_warning || v.insurance_warning) && (
                                  <div className="flex gap-2 mt-1.5">
                                    {v.ct_warning && (
                                      <span className={`text-[9px] flex items-center gap-0.5 ${v.ct_warning === 'EXPIRED' ? 'text-red-400' : 'text-amber-400'}`}>
                                        <AlertTriangle className="w-2.5 h-2.5" />CT {v.ct_warning === 'EXPIRED' ? 'expiré' : 'bientôt'} ({v.ct_expiry_date})
                                      </span>
                                    )}
                                    {v.insurance_warning && (
                                      <span className={`text-[9px] flex items-center gap-0.5 ${v.insurance_warning === 'EXPIRED' ? 'text-red-400' : 'text-amber-400'}`}>
                                        <AlertTriangle className="w-2.5 h-2.5" />Assurance {v.insurance_warning === 'EXPIRED' ? 'expirée' : 'bientôt'} ({v.insurance_expiry_date})
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {notApproved.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <XCircle className="w-3 h-3 text-amber-400" />
                              <span className="text-[10px] text-amber-400 font-medium uppercase tracking-wider">Non approuvés ({notApproved.length})</span>
                            </div>
                            {notApproved.map(v => (
                              <div key={v.id} className="p-3 rounded-lg bg-amber-500/[0.03] border border-amber-500/15 mb-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-white/70">{v.brand} {v.model}</span>
                                  <div className="flex items-center gap-1.5">
                                    <Badge className="text-[9px] bg-amber-500/15 text-amber-400">{v.status || 'EN ATTENTE'}</Badge>
                                    <a href={`/admin/vehicules?id=${v.id}`} className="text-[9px] text-[#C9A24D] hover:text-[#E6C77A] hover:underline flex items-center gap-0.5" title="Gérer ce véhicule" data-testid={`vehicle-link-${v.id}`}>
                                      <ExternalLink className="w-3 h-3" />Gérer
                                    </a>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 text-[10px] text-white/40">
                                  <span><Car className="w-3 h-3 inline mr-0.5" />{v.license_plate}</span>
                                  <span>{v.vehicle_type}</span>
                                  {v.color && <span>{v.color}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })() : <p className="text-xs text-white/30">Aucun véhicule enregistré</p>}
                </div>

                {/* Compliance */}
                <div data-testid="profile-compliance">
                  <h4 className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Conformité</h4>
                  {profile?.compliance?.issues?.length > 0 ? (
                    <div className="space-y-1">
                      {profile.compliance.issues.map((issue, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs text-red-400/80">
                          <XCircle className="w-3 h-3 shrink-0 mt-0.5" /><span>{issue}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                      <CheckCircle className="w-3.5 h-3.5" /> Tous les documents sont conformes
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* D-10: Missions Tab — D2-12: missions clickables */}
              <TabsContent value="missions" className="flex-1 overflow-y-auto px-3 py-3 md:p-4 space-y-3 mt-0">
                <MissionSection title="Missions actives" missions={driverMissions?.active} color="text-blue-400" emptyMsg="Aucune mission active" onClickMission={onOpenMissionDetail} />
                <MissionSection title="Missions futures" missions={driverMissions?.future} color="text-cyan-400" emptyMsg="Aucune mission future" onClickMission={onOpenMissionDetail} />
                <MissionSection title="Terminées aujourd'hui" missions={driverMissions?.completed_today} color="text-green-400" emptyMsg="Aucune mission terminée" onClickMission={onOpenMissionDetail} />
              </TabsContent>

              {/* D-11: Quick Assignment Tab */}
              <TabsContent value="assign" className="flex-1 overflow-y-auto px-3 py-3 md:p-4 space-y-3 mt-0">
                <h4 className="text-[10px] text-white/40 uppercase tracking-wider">Missions non assignées disponibles</h4>
                {(() => {
                  // Filter out missions already proposed to a DIFFERENT driver
                  const availableMissions = (unassignedMissions || []).filter(m =>
                    !m.proposed_driver_id || m.proposed_driver_id === driverId
                  );
                  return availableMissions.length > 0 ? (
                  <div className="space-y-2">
                    {availableMissions.map(m => (
                      <div key={m.id} className={`p-3 rounded-lg border transition ${selectedMissionForAssign?.id === m.id
                        ? 'bg-[#C9A24D]/10 border-[#C9A24D]/30' : 'bg-white/[0.03] border-white/5 hover:bg-white/5'}`}
                        data-testid={`assign-mission-${m.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <Badge className="text-[9px] bg-white/5 text-white/50 border-0">{m.type}</Badge>
                            <span className="text-xs text-white/70">{formatTime(m.start_time)}</span>
                            {m.proposed_driver_id === driverId && (
                              <Badge className="text-[9px] bg-amber-500/20 text-amber-400 border-0">Proposée</Badge>
                            )}
                          </div>
                          <Button size="sm" variant={selectedMissionForAssign?.id === m.id ? 'default' : 'outline'}
                            className={`text-[10px] h-6 ${selectedMissionForAssign?.id === m.id
                              ? 'bg-[#C9A24D] text-black hover:bg-[#b8913f]' : 'border-white/10 text-white/50 hover:text-white'}`}
                            onClick={() => handleValidate(m.id, m.source)}
                            disabled={validating}
                            data-testid={`validate-btn-${m.id}`}>
                            {validating && selectedMissionForAssign?.id === m.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <><Shield className="w-3 h-3 mr-0.5" />Vérifier</>}
                          </Button>
                        </div>
                        <p className="text-xs text-white/60 truncate">{m.pickup_address || m.ref_id || 'N/A'}</p>
                        {m.client_name && <p className="text-[10px] text-white/30 mt-0.5">{m.client_name}</p>}
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-white/30 text-center py-4">Aucune mission non assignée</p>;
                })()}

                {/* Validation result + confirm */}
                {validationResult && (
                  <div className={`p-3 rounded-lg border ${validationResult.valid
                    ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}
                    data-testid="assign-validation-result">
                    {validationResult.valid ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-emerald-400">
                          <CheckCircle className="w-4 h-4" /><span className="text-sm font-medium">Affectation valide</span>
                        </div>
                        {selectedMissionForAssign?.source === 'dispatch_tasks' && (
                          <Button onClick={handleAssignMission} disabled={assigning}
                            className="w-full bg-[#C9A24D] hover:bg-[#b8913f] text-black font-medium" data-testid="confirm-assign-btn">
                            {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-3.5 h-3.5 mr-1" />Confirmer l'affectation</>}
                          </Button>
                        )}
                        {selectedMissionForAssign?.source !== 'dispatch_tasks' && (
                          <p className="text-xs text-white/40">Affectation directe via planning pour les missions {selectedMissionForAssign?.source}.</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 text-red-400 mb-2">
                          <XCircle className="w-4 h-4" /><span className="text-sm font-medium">Problème(s) détecté(s)</span>
                        </div>
                        {validationResult.errors?.map((err, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-red-300/80 mb-1">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /><span>{err.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Phase 3 + TL: History & Timeline Tab */}
              <TabsContent value="history" className="flex-1 overflow-y-auto px-3 py-3 md:p-4 space-y-3 mt-0">
                {/* Date range selector */}
                <div className="space-y-2" data-testid="history-date-selector">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-[9px] text-white/30 uppercase">Début</label>
                      <input type="date" value={historyDate} onChange={e => setHistoryDate(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1 text-xs text-white" data-testid="history-date-start" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[9px] text-white/30 uppercase">Fin</label>
                      <input type="date" value={historyDateEnd} onChange={e => setHistoryDateEnd(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-2.5 py-1 text-xs text-white" data-testid="history-date-end" />
                    </div>
                    <button onClick={() => { const t = new Date().toISOString().slice(0, 10); setHistoryDate(t); setHistoryDateEnd(t); }}
                      className="px-2.5 py-1 mt-3 rounded-lg bg-[#C9A24D]/20 text-[#C9A24D] text-[10px] shrink-0">Aujourd'hui</button>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C9A24D]" /></div>
                ) : (
                  <>
                    {/* GPS info bar */}
                    <div className="flex items-center justify-between px-2 text-[10px]" data-testid="gps-trace-summary">
                      <div className="flex items-center gap-2">
                        <span className="text-white/50">{locationHistory?.total_points || 0} points GPS</span>
                        {locationHistory?.is_snapped && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-[9px] border border-blue-500/20"
                            data-testid="snapped-indicator">
                            <span className="w-1 h-1 rounded-full bg-blue-400" />
                            Snap-to-Road
                          </span>
                        )}
                      </div>
                      {locationHistory?.points?.length > 0 && (
                        <span className="text-white/30">
                          {formatTime(locationHistory.points[0]?.recorded_at)} → {formatTime(locationHistory.points[locationHistory.points.length - 1]?.recorded_at)}
                        </span>
                      )}
                    </div>

                    {/* TL-24: Points warning */}
                    {pointsWarning && (
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[10px] text-amber-400" data-testid="points-warning">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <span>{pointsWarning} points GPS — la plage est large, zoomez pour plus de fluidité</span>
                      </div>
                    )}

                    {/* Embedded Google Map */}
                    {locationHistory?.points?.length > 1 && (
                      <div className="rounded-lg overflow-hidden border border-white/10 relative" data-testid="trace-map-container">
                        {/* Satellite mode indicator */}
                        {satelliteMode && (
                          <div className="absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-1 rounded bg-[#C9A24D]/90 text-[#0B0B0B] text-[9px] font-bold" data-testid="satellite-mode-badge">
                            SATELLITE
                          </div>
                        )}
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: window.innerWidth < 768 ? '200px' : '250px' }}
                          center={tracePosition ? { lat: tracePosition.lat, lng: tracePosition.lng } : { lat: locationHistory.points[0]?.lat, lng: locationHistory.points[0]?.lng }}
                          zoom={satelliteMode ? 17 : 14}
                          mapTypeId={satelliteMode ? 'hybrid' : 'roadmap'}
                          options={{ styles: satelliteMode ? [] : mapStyles, disableDefaultUI: true, zoomControl: true }}
                          onLoad={(map) => {
                            traceMapRef.current = map;
                            const bounds = new window.google.maps.LatLngBounds();
                            decimatedPath.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
                            map.fitBounds(bounds);
                          }}
                          onUnmount={() => { traceMapRef.current = null; }}
                        >
                          {/* Road-following polyline: uses snap-to-road interpolated path if available, raw decimated path otherwise */}
                          <Polyline
                            path={(snappedHistoryPath || decimatedPath).map(p => ({ lat: p.lat, lng: p.lng }))}
                            options={{ strokeColor: '#3B82F6', strokeWeight: 4, strokeOpacity: 0.8 }}
                          />
                          {/* Start marker */}
                          <Marker
                            position={{ lat: locationHistory.points[0].lat, lng: locationHistory.points[0].lng }}
                            icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE, scale: satelliteMode ? 9 : 7, fillColor: satelliteMode ? '#FFFFFF' : '#10B981', fillOpacity: 1, strokeColor: satelliteMode ? '#10B981' : '#fff', strokeWeight: satelliteMode ? 3 : 2 }}
                            title="Départ"
                          />
                          {/* End marker */}
                          <Marker
                            position={{ lat: locationHistory.points[locationHistory.points.length - 1].lat, lng: locationHistory.points[locationHistory.points.length - 1].lng }}
                            icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE, scale: satelliteMode ? 9 : 7, fillColor: satelliteMode ? '#FFFFFF' : '#EF4444', fillOpacity: 1, strokeColor: satelliteMode ? '#EF4444' : '#fff', strokeWeight: satelliteMode ? 3 : 2 }}
                            title="Arrivée"
                          />
                          {/* Current position marker — animated cursor */}
                          {tracePosition && (
                            <Marker
                              position={{ lat: tracePosition.lat, lng: tracePosition.lng }}
                              icon={{ path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW, scale: satelliteMode ? 8 : 6, fillColor: satelliteMode ? '#FFFFFF' : '#C9A24D', fillOpacity: 1, strokeColor: satelliteMode ? '#C9A24D' : '#fff', strokeWeight: satelliteMode ? 3 : 2, rotation: 0 }}
                              label={{ text: `${tracePosition.timeStr || ''} ${Math.round(tracePosition.speed || 0)} km/h`, color: satelliteMode ? '#fff' : '#333', fontSize: '10px', fontWeight: '600', className: 'trace-marker-label' }}
                            />
                          )}
                          {/* TL-19: Special markers for mission events */}
                          {missionEvents.filter(e => e.lat && e.lng).map((evt, i) => {
                            const evtColors = {
                              PRISE_EN_CHARGE_CLIENT: '#10B981', DEPOSE_CLIENT: '#EF4444',
                              ARRET: '#F59E0B', EN_APPROCHE: '#8B5CF6',
                              MISSION_ACCEPTEE: '#3B82F6', FIN_DE_MISSION: '#6B7280',
                            };
                            const color = evtColors[evt.event_type] || '#9CA3AF';
                            return (
                              <Marker key={`evt-${i}`}
                                position={{ lat: evt.lat, lng: evt.lng }}
                                icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE, scale: 6, fillColor: color, fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 1.5 }}
                                title={`${evt.event_type} - ${evt.address || ''}`}
                                data-testid={`event-marker-${i}`}
                              />
                            );
                          })}
                          {/* Anomaly markers - red pulsing */}
                          {anomalies.filter(a => a.lat && a.lng).map((anomaly, i) => (
                            <Marker key={`anomaly-${i}`}
                              position={{ lat: anomaly.lat, lng: anomaly.lng }}
                              icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE, scale: 8, fillColor: '#EF4444', fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 2 }}
                              title={`${anomaly.description}`}
                              data-testid={`anomaly-marker-${i}`}
                            />
                          ))}
                          {/* TL-28: Traffic layer on modal map */}
                          {modalTraffic && <TrafficLayer />}
                        </GoogleMap>
                        {/* TL-28: Traffic toggle */}
                        <button onClick={() => setModalTraffic(!modalTraffic)}
                          className={`absolute top-2 right-2 px-2 py-1 rounded text-[9px] font-medium backdrop-blur transition-all z-10 ${
                            modalTraffic
                              ? 'bg-[#C9A24D]/20 text-[#C9A24D] border border-[#C9A24D]/30'
                              : 'bg-[#0e0e1a]/80 text-white/40 border border-white/10 hover:text-white/60'
                          }`}
                          data-testid="modal-traffic-toggle">
                          {modalTraffic ? 'Masquer trafic' : 'Trafic'}
                        </button>
                      </div>
                    )}

                    {/* Zoomable Timeline — TL-05/TL-08/TL-12/TL-22 */}
                    <ZoomableTimeline
                      points={locationHistory?.points || []}
                      segments={timeline?.segments || []}
                      events={missionEvents}
                      dateStr={historyDate}
                      isSnapped={locationHistory?.is_snapped || false}
                      onPositionChange={(pos) => {
                        setTracePosition(pos);
                        if (traceMapRef.current && pos) {
                          traceMapRef.current.panTo({ lat: pos.lat, lng: pos.lng });
                        }
                      }}
                      onEventClick={(evt) => {
                        if (evt.lat && evt.lng && traceMapRef.current) {
                          traceMapRef.current.panTo({ lat: evt.lat, lng: evt.lng });
                          traceMapRef.current.setZoom(16);
                        }
                      }}
                      onZoomLevelChange={(level) => {
                        // Auto satellite at 15min zoom (ZOOM_QUARTER = 2)
                        const isSatellite = level === 2;
                        setSatelliteMode(isSatellite);
                        if (traceMapRef.current) {
                          traceMapRef.current.setMapTypeId(isSatellite ? 'hybrid' : 'roadmap');
                          if (isSatellite) traceMapRef.current.setZoom(17);
                        }
                      }}
                    />

                    {/* Anomalies section - displayed in red */}
                    {anomalies.length > 0 && (
                      <div data-testid="anomalies-section" className="rounded-lg border border-red-500/30 bg-red-500/[0.05] overflow-hidden">
                        <div className="px-3 py-2 bg-red-500/10 border-b border-red-500/20 flex items-center justify-between">
                          <h4 className="text-[10px] text-red-400 uppercase tracking-wider font-bold flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Anomalies détectées ({anomalies.length})
                          </h4>
                          <span className="text-[9px] text-red-400/60">{anomalies.filter(a => a.severity === 'critical').length} critique(s)</span>
                        </div>
                        <div className="divide-y divide-red-500/10">
                          {anomalies.map((anomaly, i) => {
                            const isCritical = anomaly.severity === 'critical';
                            const typeLabels = {
                              DEVIATION_ITINERAIRE: { label: 'Déviation itinéraire', icon: '🚨' },
                              ARRET_NON_SIGNALE: { label: 'Arrêt non signalé', icon: '⏸' },
                            };
                            const cfg = typeLabels[anomaly.type] || { label: anomaly.type, icon: '⚠' };
                            const timestamp = anomaly.timestamp;
                            const timeStr = timestamp ? new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--:--';
                            const dateStr = timestamp ? new Date(timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';

                            return (
                              <div key={i}
                                className={`flex gap-3 px-3 py-2.5 hover:bg-red-500/10 cursor-pointer transition-colors ${isCritical ? 'bg-red-500/[0.05]' : ''}`}
                                data-testid={`anomaly-${i}`}
                                onClick={() => {
                                  if (anomaly.lat && anomaly.lng && traceMapRef.current) {
                                    traceMapRef.current.panTo({ lat: anomaly.lat, lng: anomaly.lng });
                                    traceMapRef.current.setZoom(16);
                                  }
                                }}>
                                <div className="flex flex-col items-center pt-0.5">
                                  <div className={`w-3 h-3 rounded-full ${isCritical ? 'bg-red-500 animate-pulse' : 'bg-amber-500'} ring-2 ring-[#0e0e1a]`} />
                                  {i < anomalies.length - 1 && <div className="w-0.5 flex-1 bg-red-500/20 min-h-[16px]" />}
                                </div>
                                <div className="flex-1 min-w-0 pb-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className={`text-xs font-bold ${isCritical ? 'text-red-400' : 'text-amber-400'} flex items-center gap-1`}>
                                      <span>{cfg.icon}</span>
                                      {cfg.label}
                                    </span>
                                    <span className="text-[10px] text-red-300/60 font-mono shrink-0">{dateStr} {timeStr}</span>
                                  </div>
                                  <p className="text-[10px] text-red-300/80 mt-0.5">{anomaly.description}</p>
                                  {anomaly.mission_number && (
                                    <span className="text-[9px] text-red-400/50">Mission {anomaly.mission_number}</span>
                                  )}
                                  {/* GPS position */}
                                  <div className="flex items-center gap-2 mt-0.5">
                                    {anomaly.lat && anomaly.lng ? (
                                      <span className="text-[9px] text-red-300/40 font-mono flex items-center gap-0.5">
                                        <MapPin className="w-2.5 h-2.5 text-red-400" />
                                        {anomaly.lat.toFixed(5)}, {anomaly.lng.toFixed(5)}
                                      </span>
                                    ) : (
                                      <span className="text-[9px] text-red-300/20">GPS non disponible</span>
                                    )}
                                    {anomaly.deviation_km && (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">{anomaly.deviation_km}km</span>
                                    )}
                                    {anomaly.duration_minutes && (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-bold">{anomaly.duration_minutes}min</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Mission status events - detailed timeline */}
                    {missionEvents.length > 0 && (
                      <div data-testid="mission-events-timeline">
                        <h4 className="text-[10px] text-white/40 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <Zap className="w-3 h-3 text-[#C9A24D]" />
                          Statuts mission ({missionEvents.length})
                        </h4>
                        <div className="space-y-0 rounded-lg border border-white/5 overflow-hidden">
                          {missionEvents.map((evt, i) => {
                            const statusConfig = {
                              AFFECTATION_MISSION: { label: 'Affectation mission', icon: '🎯', color: 'text-sky-400', bg: 'bg-sky-500' },
                              MISSION_ACCEPTEE: { label: 'Mission acceptée', icon: '✓', color: 'text-blue-400', bg: 'bg-blue-500' },
                              MISSION_REFUSEE: { label: 'Mission refusée', icon: '✗', color: 'text-red-400', bg: 'bg-red-500' },
                              EN_APPROCHE: { label: 'En approche', icon: '🚗', color: 'text-violet-400', bg: 'bg-violet-500' },
                              EN_ATTENTE_PRISE_EN_CHARGE_CLIENT: { label: 'Sur place - en attente', icon: '⏳', color: 'text-amber-400', bg: 'bg-amber-500' },
                              PRISE_EN_CHARGE_CLIENT: { label: 'Prise en charge client', icon: '👤', color: 'text-emerald-400', bg: 'bg-emerald-500' },
                              CLIENT_A_BORD: { label: 'Client à bord', icon: '🚘', color: 'text-teal-400', bg: 'bg-teal-500' },
                              ARRET: { label: 'Arrêt intermédiaire', icon: '⏸', color: 'text-amber-400', bg: 'bg-amber-500' },
                              DEPOSE_CLIENT: { label: 'Dépose client', icon: '📍', color: 'text-red-400', bg: 'bg-red-500' },
                              FIN_DE_MISSION: { label: 'Fin de mission', icon: '🏁', color: 'text-gray-400', bg: 'bg-gray-500' },
                              MISSION_ANNULEE: { label: 'Mission annulée', icon: '✗', color: 'text-red-400', bg: 'bg-red-500' },
                            };
                            const cfg = statusConfig[evt.event_type] || { label: evt.event_type, icon: '•', color: 'text-white/50', bg: 'bg-gray-500' };
                            const timestamp = evt.timestamp_server || evt.timestamp_driver || evt.created_at;
                            const timeStr = timestamp ? new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--:--';
                            const dateStr = timestamp ? new Date(timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
                            const hasGps = evt.lat && evt.lng;
                            
                            return (
                              <div key={i} className={`flex gap-3 px-3 py-2.5 ${i % 2 === 0 ? 'bg-white/[0.02]' : ''} hover:bg-white/[0.05] cursor-pointer transition-colors`}
                                data-testid={`mission-event-${i}`}
                                onClick={() => {
                                  if (hasGps && traceMapRef.current) {
                                    traceMapRef.current.panTo({ lat: evt.lat, lng: evt.lng });
                                    traceMapRef.current.setZoom(17);
                                  }
                                }}>
                                {/* Timeline dot + line */}
                                <div className="flex flex-col items-center pt-0.5">
                                  <div className={`w-2.5 h-2.5 rounded-full ${cfg.bg} ring-2 ring-[#0e0e1a]`} />
                                  {i < missionEvents.length - 1 && <div className="w-0.5 flex-1 bg-white/10 min-h-[20px]" />}
                                </div>
                                {/* Content */}
                                <div className="flex-1 min-w-0 pb-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className={`text-xs font-medium ${cfg.color} flex items-center gap-1`}>
                                      <span className="text-[11px]">{cfg.icon}</span>
                                      {cfg.label}
                                    </span>
                                    <span className="text-[10px] text-white/50 font-mono shrink-0">{dateStr} {timeStr}</span>
                                  </div>
                                  {/* GPS + Address */}
                                  <div className="flex items-center gap-2 mt-0.5">
                                    {hasGps ? (
                                      <span className="text-[9px] text-white/30 font-mono flex items-center gap-0.5">
                                        <MapPin className="w-2.5 h-2.5 text-[#C9A24D]" />
                                        {evt.lat.toFixed(5)}, {evt.lng.toFixed(5)}
                                      </span>
                                    ) : (
                                      <span className="text-[9px] text-white/20 flex items-center gap-0.5">
                                        <MapPin className="w-2.5 h-2.5" />GPS non disponible
                                      </span>
                                    )}
                                  </div>
                                  {evt.address_full && (
                                    <p className="text-[9px] text-white/40 truncate mt-0.5">{evt.address_full}</p>
                                  )}
                                  {evt.note && (
                                    <p className="text-[9px] text-[#C9A24D]/60 italic mt-0.5">{evt.note}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Compact activity timeline */}
                    {timeline?.segments?.length > 0 && (
                      <details className="group" data-testid="activity-timeline">
                        <summary className="text-[10px] text-white/40 uppercase tracking-wider cursor-pointer hover:text-white/60 transition-colors flex items-center gap-1">
                          <ChevronDown className="w-3 h-3 transition-transform group-open:rotate-180" />
                          Événements ({timeline.total_events || 0})
                        </summary>
                        <div className="space-y-0 max-h-[200px] overflow-y-auto mt-2">
                          {timeline.segments.map((seg, i) => {
                            const actColors = { ONLINE: 'text-emerald-400', MISSION: 'text-blue-400', OFFLINE: 'text-red-400', ARRET: 'text-amber-400' };
                            const actBg = { ONLINE: 'bg-emerald-500', MISSION: 'bg-blue-500', OFFLINE: 'bg-red-500', ARRET: 'bg-amber-500' };
                            const actLabels = { ONLINE: 'En ligne', MISSION: 'Mission', OFFLINE: 'Hors ligne', ARRET: 'Arrêt' };
                            return (
                              <div key={i} className="flex gap-2 cursor-pointer hover:bg-white/[0.03] rounded-lg -mx-1 px-1"
                                data-testid={`timeline-event-${i}`}
                                onClick={() => {
                                  if (seg.lat && seg.lng && traceMapRef.current) {
                                    traceMapRef.current.panTo({ lat: seg.lat, lng: seg.lng });
                                    traceMapRef.current.setZoom(16);
                                  }
                                }}>
                                <div className="flex flex-col items-center">
                                  <div className={`w-2 h-2 rounded-full ${actBg[seg.activity] || 'bg-gray-500'}`} />
                                  {i < timeline.segments.length - 1 && <div className="w-0.5 flex-1 bg-white/10 min-h-[16px]" />}
                                </div>
                                <div className="pb-1.5 flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className={`text-[10px] font-medium ${actColors[seg.activity] || 'text-gray-400'}`}>{actLabels[seg.activity] || seg.activity}</span>
                                    <span className="text-[9px] text-white/30">{formatTime(seg.start)}</span>
                                  </div>
                                  {seg.details && <p className="text-[9px] text-white/40 truncate">{seg.details}</p>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Phase 4: D-23 Chat Tab */}
              <TabsContent value="chat" className="flex-1 flex flex-col overflow-hidden mt-0">
                {chatLoading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#C9A24D]" /></div>
                ) : (
                  <>
                    {/* Messages area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
                      {chatMessages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-white/30">
                          <MessageSquare className="w-8 h-8 mb-2 opacity-30" />
                          <p className="text-xs">Aucun message. Commencez la conversation.</p>
                        </div>
                      ) : chatMessages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}
                          data-testid={`chat-msg-${msg.id}`}>
                          <div className={`max-w-[75%] rounded-lg px-3 py-2 ${msg.sender_role === 'admin'
                            ? 'bg-[#C9A24D]/20 border border-[#C9A24D]/30'
                            : 'bg-white/5 border border-white/10'}`}>
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`text-[10px] font-medium ${msg.sender_role === 'admin' ? 'text-[#C9A24D]' : 'text-white/60'}`}>
                                {msg.sender_name}
                              </span>
                              <span className="text-[9px] text-white/25">{formatTime(msg.created_at)}</span>
                              {msg.read_at && msg.sender_role === 'admin' && (
                                <CheckCircle className="w-2.5 h-2.5 text-emerald-400/50" />
                              )}
                            </div>
                            <p className="text-xs text-white/80 whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Input area */}
                    <div className="p-3 border-t border-white/5 shrink-0" data-testid="chat-input-area">
                      <div className="flex gap-2">
                        <Input
                          value={chatInput}
                          onChange={e => setChatInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                          placeholder="Écrire un message..."
                          className="flex-1 bg-white/5 border-white/10 text-white text-xs"
                          data-testid="chat-input"
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={!chatInput.trim() || chatSending}
                          className="bg-[#C9A24D] hover:bg-[#b8913f] text-black shrink-0"
                          size="sm"
                          data-testid="chat-send-btn"
                        >
                          {chatSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Helper: Info field display
function InfoField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 p-2 rounded bg-white/[0.03]">
      <Icon className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] text-white/30">{label}</p>
        <p className="text-xs text-white/80">{value || '—'}</p>
      </div>
    </div>
  );
}

// Helper: Mission section for D-10
function MissionSection({ title, missions, color, emptyMsg, onClickMission }) {
  if (!missions || missions.length === 0) {
    return (
      <div>
        <h4 className={`text-[10px] uppercase tracking-wider mb-1 ${color}`}>{title}</h4>
        <p className="text-xs text-white/30">{emptyMsg}</p>
      </div>
    );
  }
  return (
    <div data-testid={`mission-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <h4 className={`text-[10px] uppercase tracking-wider mb-1.5 ${color}`}>{title} ({missions.length})</h4>
      <div className="space-y-1">
        {missions.map(m => (
          <div key={m.id} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 cursor-pointer hover:bg-white/5 transition"
            onClick={() => onClickMission?.(m)}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[m.status] }} />
                <span className="text-[10px] font-medium" style={{ color: STATUS_COLORS[m.status] }}>{STATUS_LABELS[m.status]}</span>
              </div>
              <div className="flex items-center gap-1">
                <Badge className="text-[9px] bg-white/5 text-white/50 border-0">{m.type}</Badge>
                <span className="text-[10px] text-white/30">{formatTime(m.start_time)}</span>
              </div>
            </div>
            <p className="text-xs text-white/60 truncate">{m.pickup_address || m.ref_id || 'N/A'}</p>
            {m.client_name && <p className="text-[10px] text-white/30 mt-0.5">{m.client_name}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== SHUTTLE FEE SECTION (editable / locked) ==============
function ShuttleFeeSection({ mission, onFeeUpdated }) {
  const d = mission;
  const isShuttle = d?._isShuttle;

  const tripId = d?._shuttleTripId || d?.shared_trip_id || '';
  const resaCount = d?._shuttleMembers?.length || 1;
  const currentFee = d?.proposed_fee || d?._shuttleFee || 0;

  // Locked if driver has accepted (assigned_driver_id means accepted/assigned)
  const driverAccepted = !!d?.assigned_driver_id;
  const hasFee = currentFee > 0;

  const [editFee, setEditFee] = useState(String(currentFee || ''));
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setEditFee(String(currentFee || ''));
    setIsEditing(false);
  }, [currentFee, tripId]);

  // Early return AFTER hooks
  if (!isShuttle) return null;

  const handleSaveFee = async () => {
    const val = parseFloat(editFee);
    if (!val || val <= 0 || !tripId) return;
    setSaving(true);
    try {
      // Update shared_trips.proposed_fee + propagate to members
      await axios.put(
        `${API}/dispatch/pro/shuttle/${tripId}/fee`,
        { proposed_fee: val },
        { headers: getHeaders() }
      );
      setIsEditing(false);
      onFeeUpdated?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  return (
    <div className="p-3 rounded-lg bg-purple-500/[0.03] border border-purple-500/20" data-testid="shuttle-fee-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Euro className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-[10px] text-purple-300 uppercase tracking-wider font-medium">Coût transport navette</span>
        </div>
        {driverAccepted && hasFee && (
          <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <Lock className="w-2.5 h-2.5" /> Chauffeur accepté
          </span>
        )}
      </div>

      {driverAccepted && hasFee ? (
        // LOCKED: driver accepted, fee is fixed
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-purple-300">{currentFee.toFixed(2)} €</p>
            <p className="text-[10px] text-white/30">{(currentFee / resaCount).toFixed(2)} € / résa × {resaCount}</p>
          </div>
        </div>
      ) : isEditing || !hasFee ? (
        // EDITABLE: no driver or editing
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Euro className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <Input
              type="number"
              step="0.01"
              min="0"
              value={editFee}
              onChange={(e) => setEditFee(e.target.value)}
              placeholder="Montant total navette"
              className="pl-8 bg-white/[0.05] border-purple-500/30 text-white h-9 text-sm"
              data-testid="shuttle-fee-input"
            />
          </div>
          <Button size="sm" onClick={handleSaveFee} disabled={saving || !editFee || parseFloat(editFee) <= 0}
            className="bg-purple-600 hover:bg-purple-500 text-white text-[10px] h-9 px-3"
            data-testid="shuttle-fee-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Valider'}
          </Button>
          {hasFee && (
            <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setEditFee(String(currentFee)); }}
              className="text-white/40 hover:text-white/80 text-[10px] h-9">
              Annuler
            </Button>
          )}
          {editFee && parseFloat(editFee) > 0 && (
            <span className="text-[10px] text-white/30 shrink-0">
              {(parseFloat(editFee) / resaCount).toFixed(2)} € / résa
            </span>
          )}
        </div>
      ) : (
        // DISPLAY with edit button (driver not yet accepted)
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-purple-300">{currentFee.toFixed(2)} €</p>
            <p className="text-[10px] text-white/30">{(currentFee / resaCount).toFixed(2)} € / résa × {resaCount}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}
            className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10 text-[10px] h-7"
            data-testid="shuttle-fee-edit-btn">
            Modifier
          </Button>
        </div>
      )}
    </div>
  );
}

// ============== D2-06/D2-07: MISSION DETAIL MODAL ==============
function MissionDetailModal({ open, onClose, mission, fetchSnapshot, drivers }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [trackingLink, setTrackingLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showVideoExport, setShowVideoExport] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [forceAssigning, setForceAssigning] = useState(false);

  useEffect(() => {
    if (!open || !mission) return;
    setLoading(true);
    setShowOfferModal(false);
    setEditingNotes(false);
    setTrackingLink(null);
    // Shuttle missions: use synthesized data directly, no individual API call
    if (mission._isShuttle) {
      setDetail(mission);
      setNotesValue('');
      setLoading(false);
      return;
    }
    axios.get(`${API}/dispatch/pro/mission/${mission.id}?source=${mission.source}`, { headers: getHeaders() })
      .then(r => { setDetail(r.data); setNotesValue(r.data?.internal_notes || ''); })
      .catch(() => { setDetail(mission); setNotesValue(mission?.internal_notes || ''); })
      .finally(() => setLoading(false));
    // Check for existing tracking link
    axios.get(`${API}/dispatch/pro/tracking-links?mission_id=${mission.id}`, { headers: getHeaders() })
      .then(r => {
        const links = r.data?.data || [];
        const active = links.find(l => l.status === 'ACTIVE');
        if (active) {
          setTrackingLink(`${window.location.origin}/track/${active.public_token}`);
        }
      })
      .catch(() => {});
  }, [open, mission]);

  const handleUnassign = async () => {
    if (!detail) return;
    setUnassigning(true);
    try {
      // Shuttle mode: unassign all members
      if (detail._isShuttle && detail._shuttleMembers) {
        for (const m of detail._shuttleMembers) {
          await axios.post(`${API}/dispatch/pro/mission/${m.id}/unassign?source=${m.source || 'excursion_missions'}`, {}, { headers: getHeaders() }).catch(() => {});
        }
      } else {
        await axios.post(`${API}/dispatch/pro/mission/${detail.id}/unassign?source=${detail.source}`, {}, { headers: getHeaders() });
      }
      await fetchSnapshot?.();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setUnassigning(false);
  };

  const handleOfferSuccess = async () => {
    await fetchSnapshot?.();
    onClose();
  };

  const handleForceAssign = async () => {
    if (!detail) return;
    if (!window.confirm('Forcer l\'assignation du chauffeur ? (bypass acceptation app)')) return;
    setForceAssigning(true);
    try {
      const driverId = detail.proposed_driver_id || detail.assigned_driver_id;
      // Shuttle mode: force-assign all members
      if (detail._isShuttle && detail._shuttleMembers) {
        for (const m of detail._shuttleMembers) {
          const mid = m.id;
          await axios.post(`${API}/excursion-missions/${mid}/force-assign`, { driver_id: driverId }, { headers: getHeaders() }).catch(() => {});
        }
      } else {
        const source = detail.source || 'missions';
        const url = source === 'excursion_missions'
          ? `${API}/excursion-missions/${detail.id}/force-assign`
          : `${API}/planning/missions/${detail.id}/force-assign`;
        await axios.post(url, { driver_id: driverId }, { headers: getHeaders() });
      }
      await fetchSnapshot?.();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'assignation forcée');
    }
    setForceAssigning(false);
  };

  const handleGenerateTrackingLink = async () => {
    if (!detail) return;
    setGeneratingLink(true);
    try {
      const mType = detail.type === 'EXCURSION' ? 'EXCURSION' : 'VTC';
      const { data: res } = await axios.post(
        `${API}/dispatch/pro/tracking-links/generate/${mType}/${detail.id}`,
        {},
        { headers: getHeaders() }
      );
      if (res.data?.public_token) {
        const url = `${window.location.origin}/track/${res.data.public_token}`;
        setTrackingLink(url);
        navigator.clipboard.writeText(url).then(() => {
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 3000);
        });
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la génération du lien');
    }
    setGeneratingLink(false);
  };

  const handleSaveNotes = async () => {
    if (!detail) return;
    setSavingNotes(true);
    try {
      await axios.put(
        `${API}/dispatch/pro/mission/${detail.id}/notes?source=${detail.source}`,
        { internal_notes: notesValue },
        { headers: getHeaders() }
      );
      setDetail(prev => ({ ...prev, internal_notes: notesValue }));
      setEditingNotes(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setSavingNotes(false);
  };

  const EVENT_LABELS = {
    MISSION_ACCEPTEE: 'Mission acceptée', EN_APPROCHE: 'En approche',
    EN_ATTENTE_PRISE_EN_CHARGE_CLIENT: 'En attente client', PRISE_EN_CHARGE_CLIENT: 'Client à bord',
    ARRET: 'Arrêt', DEPOSE_CLIENT: 'Client déposé', FIN_DE_MISSION: 'Fin de mission',
    MISSION_REFUSEE: 'Mission refusée', MISSION_ANNULEE: 'Mission annulée',
  };

  if (!open) return null;
  const d = detail || mission;
  const isMaritime = d?.service_type === 'maritime' || d?.source === 'maritime';
  const maritimeData = d?.maritime_data || {};
  const shipName = maritimeData.ship_name || '';
  const passengers = d?.passengers || [];
  const waypoints = d?.itinerary?.waypoints || [];
  const pickupAddr = d?.pickup_address || waypoints?.[0]?.address || '';
  const dropoffAddr = d?.dropoff_address || waypoints?.slice(-1)?.[0]?.address || '';

  // Assignment section (shared between maritime and generic)
  const AssignmentSection = () => {
    const fleetStatus = d ? getFleetAwareStatus(d) : null;
    return (
    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5" data-testid="mission-assignment">
      <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Affectation</p>
      {d.assigned_driver_id ? (
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#C9A24D] shrink-0" />
              <span className="text-sm text-white/80 truncate">{d.driver_name || 'Chauffeur assigné'}</span>
              {fleetStatus === 'fleet_assigned' ? (
                <Badge className="text-[9px] bg-purple-500/15 text-purple-400 border-0 shrink-0">Attribué par flotte</Badge>
              ) : fleetStatus === 'fleet_accepted' ? (
                <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-0 shrink-0">Accepté par chauffeur</Badge>
              ) : (
                <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-0 shrink-0">Assignée</Badge>
              )}
            </div>
            {(d.license_plate || d.vehicle_name) && (
              <p className="text-[10px] text-white/40 mt-1 ml-6">{d.vehicle_name} · {d.license_plate}</p>
            )}
            {d.fleet_name && (
              <p className="text-[10px] text-[#C9A24D]/60 mt-0.5 ml-6">Flotte: {d.fleet_name}</p>
            )}
          </div>
          <Button size="sm" variant="outline"
            onClick={handleUnassign} disabled={unassigning}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] h-7 shrink-0"
            data-testid="unassign-btn">
            {unassigning ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Retirer'}
          </Button>
        </div>
      ) : fleetStatus === 'fleet_pending' ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-white/80">{d.proposed_partner_name || d.fleet_name || 'Flotte'}</span>
            <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-0">En attente d'attribution</Badge>
          </div>
          <div className="flex items-center gap-1">
            <DebugOnly>
              <Button size="sm"
                onClick={handleForceAssign} disabled={forceAssigning}
                className="bg-amber-600 hover:bg-amber-500 text-white text-[10px] h-7"
                data-testid="force-assign-fleet-btn">
                {forceAssigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <><UserCheck className="w-3 h-3 mr-1" />Forcer</>}
              </Button>
            </DebugOnly>
            <Button size="sm" variant="outline"
              onClick={handleUnassign} disabled={unassigning}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] h-7"
              data-testid="unassign-fleet-btn">
              {unassigning ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Annuler'}
            </Button>
          </div>
        </div>
      ) : d.proposed_driver_name ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-white/80">{d.proposed_driver_name}</span>
            <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-0">Proposée</Badge>
          </div>
          <div className="flex items-center gap-1">
            <DebugOnly>
              <Button size="sm"
                onClick={handleForceAssign} disabled={forceAssigning}
                className="bg-amber-600 hover:bg-amber-500 text-white text-[10px] h-7"
                data-testid="force-assign-btn">
                {forceAssigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <><UserCheck className="w-3 h-3 mr-1" />Forcer</>}
              </Button>
            </DebugOnly>
            <Button size="sm" variant="outline"
              onClick={handleUnassign} disabled={unassigning}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] h-7"
              data-testid="unassign-btn">
              {unassigning ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Annuler'}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {d.type === 'EXCURSION' && d.excursion_status && d.excursion_status !== 'confirmed' ? (
            <div className="text-xs text-amber-400/70 py-1">
              Réservation non confirmée ({d.excursion_status}) — affectation impossible
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-xs text-amber-400/70">Non assignée</span>
              <Button size="sm" className="bg-[#C9A24D] hover:bg-[#b8913f] text-black text-[10px] h-7"
                onClick={() => setShowOfferModal(true)}
                data-testid="assign-from-detail-btn">
                <Euro className="w-3 h-3 mr-1" />Proposer une offre
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
  };

  // Editable notes section (shared)
  const NotesSection = () => (
    <div className={`rounded-lg border p-3 ${isMaritime ? 'bg-cyan-900/10 border-cyan-600/20 md:col-span-2' : 'bg-white/[0.03] border-white/5'}`} data-testid="mission-notes-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className={`w-3.5 h-3.5 ${isMaritime ? 'text-cyan-400' : 'text-[#C9A24D]'}`} />
          <h4 className={`font-semibold text-sm ${isMaritime ? 'text-cyan-300' : 'text-white/80'}`}>Notes chauffeur</h4>
        </div>
        {!editingNotes ? (
          <Button size="sm" variant="ghost" className="text-[10px] h-6 text-white/40 hover:text-white/80"
            onClick={() => setEditingNotes(true)} data-testid="edit-notes-btn">
            Modifier
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="text-[10px] h-6 text-white/40 hover:text-white/80"
              onClick={() => { setEditingNotes(false); setNotesValue(d.internal_notes || ''); }}
              data-testid="cancel-notes-btn">
              Annuler
            </Button>
            <Button size="sm" className="text-[10px] h-6 bg-[#C9A24D] hover:bg-[#b8913f] text-black"
              onClick={handleSaveNotes} disabled={savingNotes}
              data-testid="save-notes-btn">
              {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Enregistrer
            </Button>
          </div>
        )}
      </div>
      {editingNotes ? (
        <textarea
          className="w-full bg-black/30 border border-white/10 rounded text-xs text-white/80 p-2 min-h-[100px] resize-y focus:outline-none focus:border-[#C9A24D]/40"
          value={notesValue}
          onChange={(e) => setNotesValue(e.target.value)}
          data-testid="notes-textarea"
        />
      ) : (
        <pre className="text-xs text-white/70 whitespace-pre-wrap font-sans leading-relaxed max-h-[200px] overflow-y-auto">
          {d.internal_notes || <span className="text-white/30 italic">Aucune note</span>}
        </pre>
      )}
    </div>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={`bg-[#0e0e1a] border-white/10 text-white ${isMaritime ? 'max-w-3xl w-[98vw] md:w-[95vw]' : 'max-w-2xl w-[98vw] md:w-[90vw]'} max-h-[90vh] md:max-h-[85vh] overflow-y-auto rounded-xl`} data-testid="mission-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            {isMaritime ? (
              <>
                <span className="bg-cyan-700 text-white text-xs font-bold w-6 h-6 flex items-center justify-center rounded">M</span>
                <span className="text-[#C9A24D] font-mono">{d?.ref_id || d?.id?.slice(0, 8)}</span>
                <Badge variant="outline" className="border-[#C9A24D]/40 text-white/60">
                  {d?.status || '—'}
                </Badge>
              </>
            ) : d?._isShuttle ? (
              <>
                <Anchor className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 font-semibold">Navette</span>
                <Badge className="text-[9px] bg-purple-500/15 text-purple-300 border-0">{d._shuttleMembers?.length || 0} résa · {d._shuttleTotalPax || 0} pax</Badge>
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4 text-[#C9A24D]" />
                Mission {d?.type} — {d?.ref_id || d?.id?.slice(0, 8)}
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C9A24D]" /></div>
        ) : d ? (
          isMaritime ? (
            /* ========= MARITIME LAYOUT (same as BookingDetailsModal) ========= */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Ship section */}
              {shipName && (
                <div className="bg-cyan-900/20 rounded-lg border border-cyan-600/30 p-3 md:col-span-2" data-testid="maritime-ship-section">
                  <div className="flex items-center gap-2 mb-1">
                    <Anchor className="w-4 h-4 text-cyan-400" />
                    <h4 className="font-semibold text-cyan-300 text-sm">Navire</h4>
                  </div>
                  <div className="text-base font-bold text-white">{shipName}</div>
                  <div className="flex gap-4 text-xs text-white/40 mt-1">
                    {maritimeData.original_client && maritimeData.original_client !== shipName && (
                      <span>Armateur : {maritimeData.original_client}</span>
                    )}
                    {maritimeData.dossier && (
                      <span>Dossier : {maritimeData.dossier} / Mission : {maritimeData.mission_num}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Assignment section */}
              <div className="md:col-span-2">
                {AssignmentSection()}
              </div>

              {/* Tracking */}
              <div className="bg-[#0B0B0B]/60 rounded-lg border border-[#C9A24D]/10 p-3" data-testid="maritime-tracking-section">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-3.5 h-3.5 text-[#C9A24D]" />
                  <h4 className="font-semibold text-sm">Tracking</h4>
                </div>
                {trackingLink ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" className="bg-[#C9A24D] hover:bg-[#b8913f] text-black h-7 text-[10px]"
                      onClick={() => window.open(trackingLink, '_blank')} data-testid="tracking-open-btn">
                      <ExternalLink className="w-3 h-3 mr-1" /> Ouvrir
                    </Button>
                    <Button size="sm" variant="outline" className="border-[#C9A24D]/40 text-[#C9A24D] h-7 text-[10px]"
                      onClick={async () => { try { await navigator.clipboard.writeText(trackingLink); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); } catch {} }}>
                      <Copy className="w-3 h-3 mr-1" /> {linkCopied ? 'Copié !' : 'Copier'}
                    </Button>
                  </div>
                ) : d.assigned_driver_id ? (
                  <Button size="sm" onClick={handleGenerateTrackingLink} disabled={generatingLink}
                    className="bg-[#C9A24D] hover:bg-[#b8913f] text-black h-7 text-[10px]" data-testid="generate-tracking-link-btn">
                    {generatingLink ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Radio className="w-3 h-3 mr-1" />}
                    Générer le tracking
                  </Button>
                ) : (
                  <div className="text-white/40 text-xs">Assignez un chauffeur d'abord</div>
                )}
              </div>

              {/* Export Vidéo GPS — Maritime */}
              {d.assigned_driver_id && (
                <div className="bg-[#0B0B0B]/60 rounded-lg border border-violet-500/20 p-3" data-testid="maritime-video-export-section">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Video className="w-3.5 h-3.5 text-violet-400" />
                      <h4 className="font-semibold text-sm text-white/80">Export Vidéo GPS</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm"
                        onClick={() => setShowAnalysis(true)}
                        className="bg-[#C9A24D]/15 hover:bg-[#C9A24D]/25 text-[#C9A24D] border border-[#C9A24D]/20 text-[10px] h-7"
                        data-testid="mission-analyse-btn-maritime">
                        <Activity className="w-3 h-3 mr-1" />Analyse
                      </Button>
                      <Button size="sm"
                        onClick={() => setShowVideoExport(true)}
                        className="bg-violet-500/15 hover:bg-violet-500/25 text-violet-400 border border-violet-500/20 text-[10px] h-7"
                        data-testid="mission-video-export-btn">
                        <Video className="w-3 h-3 mr-1" />Export Vidéo
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-white/20">Analyse du trajet (vitesse, retard, score) · replay MP4 de la trajectoire</p>
                </div>
              )}

              {/* Passengers */}
              {passengers.length > 0 && (
                <div className="bg-[#0B0B0B]/60 rounded-lg border border-[#C9A24D]/10 p-3" data-testid="maritime-passengers-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-3.5 h-3.5 text-[#C9A24D]" />
                    <h4 className="font-semibold text-sm">Passagers ({passengers.length})</h4>
                  </div>
                  <div className="space-y-0.5 max-h-[120px] overflow-y-auto">
                    {passengers.map((p, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs">
                        <span className="text-white/30 w-4 text-right">{i + 1}.</span>
                        <span className="text-white/80">{typeof p === 'string' ? p : (p.note || `${p.first_name || ''} ${p.last_name || ''}`.trim() || `${p.name || ''}`) || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Itinerary */}
              <div className="bg-[#0B0B0B]/60 rounded-lg border border-[#C9A24D]/10 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[#C9A24D]" />
                    <h4 className="font-semibold text-sm">Itinéraire</h4>
                  </div>
                  {(d.itinerary?.total_distance_km || d.itinerary?.estimated_duration_minutes) && (
                    <div className="flex items-center gap-2 text-[10px] text-white/40">
                      {d.itinerary.total_distance_km && <span>{Number(d.itinerary.total_distance_km).toFixed(0)} km</span>}
                      {d.itinerary.estimated_duration_minutes && (
                        <span>~{Math.floor(d.itinerary.estimated_duration_minutes / 60)}h{String(d.itinerary.estimated_duration_minutes % 60).padStart(2, '0')}</span>
                      )}
                      {d.itinerary.route_summary && <span className="text-[#C9A24D]/60">{d.itinerary.route_summary}</span>}
                    </div>
                  )}
                </div>

                {waypoints.length > 0 ? (
                  <div className="space-y-0">
                    {waypoints.map((wp, idx) => {
                      const isFirst = idx === 0;
                      const isLast = idx === waypoints.length - 1;
                      const dotColor = isFirst ? 'bg-emerald-500' : isLast ? 'bg-red-400' : 'bg-amber-400';
                      const label = isFirst ? 'Départ' : isLast ? 'Arrivée' : `Étape ${idx}`;
                      return (
                        <div key={wp.id || idx} className="flex gap-2.5">
                          <div className="flex flex-col items-center pt-1 shrink-0">
                            <div className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
                            {!isLast && <div className="w-0.5 flex-1 bg-white/10 min-h-[18px] my-0.5" />}
                          </div>
                          <div className="pb-2 flex-1 min-w-0">
                            <div className="text-[10px] text-white/30 uppercase tracking-wider">{label}</div>
                            <div className="text-xs text-white/80 leading-snug">{wp.address || '—'}</div>
                            {wp.name && wp.name !== wp.address && (
                              <div className="text-[10px] text-[#C9A24D]/70 mt-0.5">{wp.name}</div>
                            )}
                            {(wp.flight_number || wp.train_number) && (
                              <div className="text-[10px] text-blue-400/70 mt-0.5">
                                {wp.flight_number && `Vol : ${wp.flight_number}`}
                                {wp.train_number && `Train : ${wp.train_number}`}
                                {wp.terminal && ` · Terminal ${wp.terminal}`}
                              </div>
                            )}
                            {wp.instructions && (
                              <div className="text-[10px] text-white/40 italic mt-0.5">"{wp.instructions}"</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-1.5 text-xs">
                    <div>
                      <div className="text-white/40 text-[10px]">Départ</div>
                      <div className="text-white/80">{pickupAddr || '—'}</div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px]">Arrivée</div>
                      <div className="text-white/80">{dropoffAddr || '—'}</div>
                    </div>
                  </div>
                )}

                {(d.estimated_arrival_datetime || d.start_time) && (
                  <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5 text-xs">
                    {d.estimated_arrival_datetime && (
                      <div>
                        <div className="text-white/40 text-[10px]">Arrivée estimée</div>
                        <div className="text-white/80">{new Date(d.estimated_arrival_datetime).toLocaleString('fr-FR')}</div>
                      </div>
                    )}
                    {d.start_time && (
                      <div>
                        <div className="text-white/40 text-[10px]">Date & heure</div>
                        <div className="text-white/80">{new Date(d.start_time).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Editable Notes */}
              {NotesSection()}

              {/* Proposed fee - hidden for internal */}
              {(d.proposed_fee || d.driver_fee) && d.target_type !== 'INTERNAL' && (
                <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-white/30">Montant chauffeur</span>
                    <span className="text-sm font-semibold text-[#C9A24D]">{d.driver_fee || d.proposed_fee} €</span>
                  </div>
                </div>
              )}

              {/* Events */}
              {d.events?.length > 0 && (
                <div className="md:col-span-2" data-testid="mission-events">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Événements ({d.events.length})</p>
                  <div className="space-y-0">
                    {d.events.map((evt, i) => (
                      <div key={evt.id} className="flex gap-3" data-testid={`mission-event-${i}`}>
                        <div className="flex flex-col items-center">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#C9A24D]" />
                          {i < d.events.length - 1 && <div className="w-0.5 flex-1 bg-white/10 min-h-[24px]" />}
                        </div>
                        <div className="pb-2 flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-white/80">{EVENT_LABELS[evt.event_type] || evt.event_type}</span>
                            <span className="text-[10px] text-white/30">{formatTime(evt.timestamp)}</span>
                          </div>
                          {evt.address && <p className="text-[10px] text-white/40 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 shrink-0" />{evt.address}</p>}
                          {evt.note && <p className="text-[10px] text-amber-400/60 italic">"{evt.note}"</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ========= GENERIC LAYOUT ========= */
            <div className="space-y-4">
              {/* Section 1: General info */}
              <div className="grid grid-cols-2 gap-2 text-xs" data-testid="mission-info">
                <div className="p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-[10px] text-white/30">Type</p>
                  <MissionTypeBadge mission={d} className="mt-0.5" />
                </div>
                <div className="p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-[10px] text-white/30">Date & heure</p>
                  <p className="text-white/80 mt-0.5">{d.start_time ? new Date(d.start_time).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                </div>
                {d.experience_name && (
                  <div className="col-span-2 p-2 rounded-lg bg-white/[0.03]">
                    <p className="text-[10px] text-white/30">Expérience</p>
                    <p className="text-white/80 mt-0.5 font-medium">{d.experience_name}</p>
                  </div>
                )}
                <div className="col-span-2 p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-[10px] text-white/30">Pickup</p>
                  <p className="text-white/80 mt-0.5">{d.pickup_address || '—'}</p>
                </div>
                {d.dropoff_address && (
                  <div className="col-span-2 p-2 rounded-lg bg-white/[0.03]">
                    <p className="text-[10px] text-white/30">Dropoff</p>
                    <p className="text-white/80 mt-0.5">{d.dropoff_address}</p>
                  </div>
                )}
                <div className="p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-[10px] text-white/30">Client</p>
                  <p className="text-white/80 mt-0.5">{d.client_name || '—'}</p>
                </div>
                <div className="p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-[10px] text-white/30">Passagers</p>
                  <p className="text-white/80 mt-0.5">{d.passenger_count || 0} pax</p>
                </div>
                {d.email && (
                  <div className="p-2 rounded-lg bg-white/[0.03]">
                    <p className="text-[10px] text-white/30">Email</p>
                    <a href={`mailto:${d.email}`} className="text-xs text-blue-400 hover:text-blue-300 hover:underline mt-0.5 block truncate" data-testid="mission-client-email">{d.email}</a>
                  </div>
                )}
                {d.phone_number && (
                  <div className="p-2 rounded-lg bg-white/[0.03]">
                    <p className="text-[10px] text-white/30">Téléphone</p>
                    <a href={`tel:${d.phone_number}`} className="text-xs text-blue-400 hover:text-blue-300 hover:underline mt-0.5 block" data-testid="mission-client-phone">{d.phone_number}</a>
                  </div>
                )}
              </div>

              {/* Shuttle members list — only for navette */}
              {d._isShuttle && d._shuttleMembers && (
                <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.03] p-3" data-testid="shuttle-passengers-list">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-3.5 h-3.5 text-purple-400" />
                    <h4 className="font-semibold text-sm text-purple-300">Passagers ({d._shuttleMembers.length} réservations)</h4>
                  </div>
                  <div className="space-y-1.5">
                    {d._shuttleMembers.map(m => (
                      <div key={m.id} className="flex items-center justify-between p-2 rounded-md bg-white/[0.02] border border-white/5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[m.status] || '#6B7280' }} />
                          <span className="text-xs text-white/70 truncate">{m.client_name || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 text-[10px] text-white/30">
                          <span>{m.passenger_count || 0} pax</span>
                          {m.phone_number && <a href={`tel:${m.phone_number}`} className="text-blue-400">{m.phone_number}</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Itinerary — waypoints + distance/duration */}
              <div className="bg-[#0B0B0B]/60 rounded-lg border border-[#C9A24D]/10 p-3" data-testid="mission-itinerary">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[#C9A24D]" />
                    <h4 className="font-semibold text-sm">Itinéraire</h4>
                  </div>
                  {(d.itinerary?.total_distance_km || d.itinerary?.estimated_duration_minutes) && (
                    <div className="flex items-center gap-2 text-[10px] text-white/40" data-testid="itinerary-meta">
                      {d.itinerary.total_distance_km && <span>{Number(d.itinerary.total_distance_km).toFixed(0)} km</span>}
                      {d.itinerary.estimated_duration_minutes && (
                        <span>~{Math.floor(d.itinerary.estimated_duration_minutes / 60)}h{String(d.itinerary.estimated_duration_minutes % 60).padStart(2, '0')}</span>
                      )}
                      {d.itinerary.route_summary && <span className="text-[#C9A24D]/60">{d.itinerary.route_summary}</span>}
                    </div>
                  )}
                </div>

                {waypoints.length > 0 ? (
                  <div className="space-y-0" data-testid="itinerary-waypoints">
                    {waypoints.map((wp, idx) => {
                      const isFirst = idx === 0;
                      const isLast = idx === waypoints.length - 1;
                      const dotColor = isFirst ? 'bg-emerald-500' : isLast ? 'bg-red-400' : 'bg-amber-400';
                      const label = isFirst ? 'Départ' : isLast ? 'Arrivée' : `Étape ${idx}`;
                      return (
                        <div key={wp.id || idx} className="flex gap-2.5" data-testid={`waypoint-${idx}`}>
                          <div className="flex flex-col items-center pt-1 shrink-0">
                            <div className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
                            {!isLast && <div className="w-0.5 flex-1 bg-white/10 min-h-[18px] my-0.5" />}
                          </div>
                          <div className="pb-2 flex-1 min-w-0">
                            <div className="text-[10px] text-white/30 uppercase tracking-wider">{label}</div>
                            <div className="text-xs text-white/80 leading-snug">{wp.address || '—'}</div>
                            {wp.name && wp.name !== wp.address && (
                              <div className="text-[10px] text-[#C9A24D]/70 mt-0.5">{wp.name}</div>
                            )}
                            {(wp.flight_number || wp.train_number) && (
                              <div className="text-[10px] text-blue-400/70 mt-0.5">
                                {wp.flight_number && `Vol : ${wp.flight_number}`}
                                {wp.train_number && `Train : ${wp.train_number}`}
                                {wp.terminal && ` · Terminal ${wp.terminal}`}
                              </div>
                            )}
                            {wp.instructions && (
                              <div className="text-[10px] text-white/40 italic mt-0.5">"{wp.instructions}"</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-1.5 text-xs">
                    <div>
                      <div className="text-white/40 text-[10px]">Départ</div>
                      <div className="text-white/80">{pickupAddr || '—'}</div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px]">Arrivée</div>
                      <div className="text-white/80">{dropoffAddr || '—'}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Assignment */}
              {AssignmentSection()}

              {/* Shuttle Transport Cost — editable inline or locked if driver accepted */}
              <ShuttleFeeSection mission={d} onFeeUpdated={async () => { await fetchSnapshot?.(); }} />

              {/* Proposed fee display (non-shuttle, non-internal) */}
              {!d._isShuttle && (d.proposed_fee || d.driver_fee) && d.target_type !== 'INTERNAL' && (
                <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-white/30">Montant chauffeur</span>
                    <span className="text-sm font-semibold text-[#C9A24D]">{d.driver_fee || d.proposed_fee} €</span>
                  </div>
                  {d.target_type && (
                    <p className="text-[9px] text-white/30 mt-0.5">Type: {d.target_type === 'INTERNAL' ? 'Interne' : d.target_type === 'FLEET' ? 'Flotte' : 'Indépendant'}</p>
                  )}
                </div>
              )}

              {/* Tracking link - only when driver is assigned */}
              {d.assigned_driver_id && (
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5" data-testid="tracking-link-section">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Lien de suivi public</p>
                    <Button size="sm" onClick={handleGenerateTrackingLink} disabled={generatingLink}
                      className="bg-[#C9A24D]/15 hover:bg-[#C9A24D]/25 text-[#C9A24D] border border-[#C9A24D]/20 text-[10px] h-7"
                      data-testid="generate-tracking-link-btn">
                      {generatingLink ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Radio className="w-3 h-3 mr-1" />}
                      {trackingLink ? (linkCopied ? 'Copié !' : 'Copier le lien') : 'Générer le lien'}
                    </Button>
                  </div>
                  {trackingLink && (
                    <p className="text-[10px] text-emerald-400/70 mt-1.5 truncate">{trackingLink}</p>
                  )}
                </div>
              )}

              {/* Export Vidéo GPS */}
              {d.assigned_driver_id && (
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5" data-testid="mission-video-export-section">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Trajet GPS</p>
                    <div className="flex items-center gap-2">
                      <Button size="sm"
                        onClick={() => setShowAnalysis(true)}
                        className="bg-[#C9A24D]/15 hover:bg-[#C9A24D]/25 text-[#C9A24D] border border-[#C9A24D]/20 text-[10px] h-7"
                        data-testid="mission-analyse-btn">
                        <Activity className="w-3 h-3 mr-1" />Analyse
                      </Button>
                      <Button size="sm"
                        onClick={() => setShowVideoExport(true)}
                        className="bg-violet-500/15 hover:bg-violet-500/25 text-violet-400 border border-violet-500/20 text-[10px] h-7"
                        data-testid="mission-video-export-btn">
                        <Video className="w-3 h-3 mr-1" />Export Vidéo
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-white/20 mt-1">Analyse du trajet (vitesse, retard, score) · replay MP4</p>
                </div>
              )}

              {/* D2-07: Mission events */}
              {d.events?.length > 0 && (
                <div data-testid="mission-events">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Événements ({d.events.length})</p>
                  <div className="space-y-0">
                    {d.events.map((evt, i) => (
                      <div key={evt.id} className="flex gap-3" data-testid={`mission-event-${i}`}>
                        <div className="flex flex-col items-center">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#C9A24D]" />
                          {i < d.events.length - 1 && <div className="w-0.5 flex-1 bg-white/10 min-h-[24px]" />}
                        </div>
                        <div className="pb-2 flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-white/80">{EVENT_LABELS[evt.event_type] || evt.event_type}</span>
                            <span className="text-[10px] text-white/30">{formatTime(evt.timestamp)}</span>
                          </div>
                          {evt.address && <p className="text-[10px] text-white/40 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 shrink-0" />{evt.address}</p>}
                          {evt.note && <p className="text-[10px] text-amber-400/60 italic">"{evt.note}"</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[d.status] || '#6B7280' }} />
                <span className="text-xs" style={{ color: STATUS_COLORS[d.status] }}>{STATUS_LABELS[d.status] || d.status}</span>
              </div>
            </div>
          )
        ) : null}
      </DialogContent>
    </Dialog>

      {/* MissionOfferModal */}
      <MissionOfferModal
        open={showOfferModal}
        onClose={() => setShowOfferModal(false)}
        mission={d}
        onSuccess={handleOfferSuccess}
      />

      {/* GPS Video Export Modal */}
      {showVideoExport && d && (
        <GpsVideoExportModal
          fetchPointsUrl={`${API}/admin/missions/${d.id}/locations`}
          driverName={d.driver_name || d.proposed_driver_name || 'Chauffeur'}
          subtitle={`Mission #${d.ref_id || d.id?.slice(0, 8)} · ${d.start_time ? new Date(d.start_time).toLocaleDateString('fr-FR') : ''}`}
          filename={`mission_${d.ref_id || d.id?.slice(0, 8)}`}
          onClose={() => setShowVideoExport(false)}
        />
      )}

      {/* Mission Analysis Modal */}
      <MissionAnalysisModal
        open={showAnalysis}
        missionId={d?.id}
        missionRef={d?.ref_id || d?.maritime_ref || d?.id?.slice(0, 8)}
        getAuthHeaders={getHeaders}
        onClose={() => setShowAnalysis(false)}
      />
    </>
  );
}

// ============== MAIN DISPATCH PAGE ==============
export default function PartnerDispatchMapPage() {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY, libraries: LIBRARIES });
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [focusedDriver, setFocusedDriver] = useState(null);
  const [infoWindowDriver, setInfoWindowDriver] = useState(null);
  const [profileModal, setProfileModal] = useState({ open: false, driverId: null });
  const [urlParamHandled, setUrlParamHandled] = useState(false);
  const [complianceModal, setComplianceModal] = useState(false);  // D-30
  const [complianceData, setComplianceData] = useState(null);
  const [anomalyDashboard, setAnomalyDashboard] = useState(null);  // Anomaly compliance
  const [auditModal, setAuditModal] = useState(false);  // D-31
  const [auditData, setAuditData] = useState(null);
  const [showTraffic, setShowTraffic] = useState(false);  // D2-22
  const [missionDate, setMissionDate] = useState(() => {
    // Paris-local date (fr-CA produces YYYY-MM-DD) — avoids the UTC off-by-one
    // that made the default day wrong between 00h-02h Paris time.
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });
  const [missionDateEnd, setMissionDateEnd] = useState('');  // Open-ended by default
  const [missionType, setMissionType] = useState(null);  // D2-04
  const [filterParams, setFilterParams] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const wsRef = useRef(null);
  // Animation state: last known positions per driver
  const driverPositionsRef = useRef({});
  // PERF: Single global animation loop — replaces per-driver rAF
  const globalAnimRef = useRef(null);
  const activeAnimationsRef = useRef({}); // {driverId: {startPos, endPos, startTime, duration}}
  const lastFrameTimeRef = useRef(0); // throttle to ~20fps
  // PERF: Animated positions stored in ref (direct native marker updates, no React)
  const animatedPosRef = useRef({});
  // PERF: Native Google Maps markers — completely independent from React render cycle
  const nativeMarkersRef = useRef({});
  // PERF: Track driver data for native marker icon updates
  const driverDataRef = useRef({});
  // State only for InfoWindow (one at a time, minimal re-render)
  const [infoWindowPos, setInfoWindowPos] = useState(null);
  // PERF: Combined interaction flag — pauses all animations during zoom/drag
  const isInteractingRef = useRef(false);
  const interactionTimeoutRef = useRef(null);
  const pendingUpdatesRef = useRef([]);
  // PERF: Native mission markers (avoid React re-renders inside GoogleMap)
  const missionMarkersRef = useRef({});
  // PERF: Track last icon state per driver to skip redundant setIcon() calls
  const markerIconStateRef = useRef({});
  // PERF: MarkerClusterer instance
  const clustererRef = useRef(null);
  // DEBUG: Performance monitoring refs
  const perfRef = useRef({ fps: 0, frameTime: 0, frames: 0, lastFpsTime: 0, snapshotLatency: 0 });
  const [debugStats, setDebugStats] = useState(null);
  const [debugMode, setDebugMode] = useState(searchParams.get('debug-map') === 'true');
  const debugEnabled = debugMode;

  // Helper: build marker icon config
  const buildMarkerIcon = useCallback((driver, isFocused, heading) => {
    const hasHeading = heading != null;
    const markerColor = driver.is_stale ? STALE_COLOR : (MARKER_COLORS[driver.calculated_status] || '#6B7280');
    const isSat = mapRef.current?.getMapTypeId?.() === 'hybrid' || mapRef.current?.getMapTypeId?.() === 'satellite';
    return {
      path: hasHeading
        ? window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW
        : window.google?.maps?.SymbolPath?.CIRCLE,
      rotation: hasHeading ? heading : 0,
      scale: hasHeading ? (isFocused ? 7 : 5) * (isSat ? 1.3 : 1) : (isFocused ? 14 : 10) * (isSat ? 1.2 : 1),
      fillColor: isSat ? '#FFFFFF' : markerColor,
      fillOpacity: driver.calculated_status === 'DECONNECTE' ? 0.7 : 1,
      strokeColor: isSat ? markerColor : (isFocused ? '#C9A24D' : '#fff'),
      strokeWeight: isSat ? 3 : (isFocused ? 3 : 2),
    };
  }, []);

  // Register animation into the global loop (no per-driver rAF)
  const animateMarker = useCallback((driverId, startPos, endPos, duration = 2500) => {
    // Buffer updates during interaction — apply after idle
    if (isInteractingRef.current) {
      pendingUpdatesRef.current.push({ driverId, endPos });
      animatedPosRef.current[driverId] = endPos;
      driverPositionsRef.current[driverId] = endPos;
      return;
    }
    // Use current animated position as true start (not last snapshot pos)
    const trueStart = animatedPosRef.current[driverId] || startPos;
    // Skip micro-movements under 10 meters
    if (haversineMeters(trueStart.lat, trueStart.lng, endPos.lat, endPos.lng) < 10) {
      animatedPosRef.current[driverId] = endPos;
      driverPositionsRef.current[driverId] = endPos;
      const marker = nativeMarkersRef.current[driverId];
      if (marker) marker.setPosition({ lat: endPos.lat, lng: endPos.lng });
      return;
    }
    // Cancel any running animation for this driver and restart from current visual position
    activeAnimationsRef.current[driverId] = {
      startPos: { lat: trueStart.lat, lng: trueStart.lng, heading: trueStart.heading },
      endPos, startTime: performance.now(), duration,
    };
    driverPositionsRef.current[driverId] = endPos;
  }, []);

  // PERF: Single global animation loop — one rAF for all drivers, throttled to ~20fps
  useEffect(() => {
    const FPS_INTERVAL = 50; // ~20fps for marker updates
    const loop = (now) => {
      globalAnimRef.current = requestAnimationFrame(loop);

      // FPS measurement (always runs at ~60fps for accurate counting)
      const perf = perfRef.current;
      perf.frames++;
      if (now - perf.lastFpsTime >= 1000) {
        perf.fps = perf.frames;
        perf.frameTime = perf.frames > 0 ? Math.round(1000 / perf.frames) : 0;
        perf.frames = 0;
        perf.lastFpsTime = now;
        // Push stats to React state (1x/sec, minimal overhead)
        if (debugEnabled) {
          setDebugStats({
            fps: perf.fps,
            frameTime: perf.frameTime,
            markers: Object.keys(nativeMarkersRef.current).length,
            animations: Object.keys(activeAnimationsRef.current).length,
            snapshotLatency: perf.snapshotLatency,
            interacting: isInteractingRef.current,
          });
        }
      }

      // Pause marker updates during map interaction
      if (isInteractingRef.current) return;
      // Throttle: skip frame if < 50ms since last update
      if (now - lastFrameTimeRef.current < FPS_INTERVAL) return;
      lastFrameTimeRef.current = now;

      const anims = activeAnimationsRef.current;
      const keys = Object.keys(anims);
      if (keys.length === 0) return;

      for (const driverId of keys) {
        const { startPos, endPos, startTime, duration } = anims[driverId];
        const rawP = Math.min((now - startTime) / duration, 1);
        const t = easeInOut(rawP);
        const lat = startPos.lat + (endPos.lat - startPos.lat) * t;
        const lng = startPos.lng + (endPos.lng - startPos.lng) * t;
        let heading = endPos.heading;
        if (startPos.heading != null && endPos.heading != null) {
          heading = interpolateAngle(startPos.heading, endPos.heading, t);
        }
        animatedPosRef.current[driverId] = { lat, lng, heading };
        const marker = nativeMarkersRef.current[driverId];
        if (marker) {
          marker.setPosition({ lat, lng });
          if (heading != null) {
            const icon = marker.getIcon();
            if (icon) marker.setIcon({ ...icon, rotation: heading });
          }
        }
        if (rawP >= 1) delete anims[driverId];
      }
    };
    globalAnimRef.current = requestAnimationFrame(loop);
    return () => { if (globalAnimRef.current) cancelAnimationFrame(globalAnimRef.current); };
  }, [debugEnabled]);

  // PERF: Create/update/remove native markers when driver data changes
  useEffect(() => {
    if (!mapReady || !mapRef.current || !data?.drivers || !window.google) return;
    const currentIds = new Set();
    let markersChanged = false;

    data.drivers.filter(d => d.lat != null).forEach(driver => {
      const id = driver.driver_id;
      currentIds.add(id);
      driverDataRef.current[id] = driver;
      const pos = animatedPosRef.current[id] || { lat: driver.lat, lng: driver.lng };
      const heading = animatedPosRef.current[id]?.heading;
      const isFocused = focusedDriver === id;

      // PERF: Build a cache key to skip redundant setIcon() calls
      const iconKey = `${driver.calculated_status}_${driver.active_mission_status || ''}_${driver.is_stale}_${isFocused}_${heading != null}`;

      if (nativeMarkersRef.current[id]) {
        // Only call setIcon if status/focus/heading actually changed
        if (markerIconStateRef.current[id] !== iconKey) {
          nativeMarkersRef.current[id].setIcon(buildMarkerIcon(driver, isFocused, heading));
          markerIconStateRef.current[id] = iconKey;
        }
      } else {
        // Create new native marker — added to clusterer, not directly to map
        const marker = new window.google.maps.Marker({
          position: pos,
          icon: buildMarkerIcon(driver, isFocused, heading),
          title: `${driver.full_name} — ${DRIVER_STATUSES[driver.calculated_status]?.label}${driver.active_mission_status ? ` (${MISSION_STATUS_LABELS[driver.active_mission_status] || driver.active_mission_status})` : ''}`,
          optimized: true,
        });
        marker.addListener('click', () => {
          const d = driverDataRef.current[id];
          setSelectedDriver(d);
          setFocusedDriver(id);
          setInfoWindowDriver(prev => prev === id ? null : id);
          const p = animatedPosRef.current[id] || { lat: d?.lat, lng: d?.lng };
          setInfoWindowPos({ lat: p.lat, lng: p.lng });
        });
        nativeMarkersRef.current[id] = marker;
        markerIconStateRef.current[id] = iconKey;
        if (clustererRef.current) clustererRef.current.addMarker(marker, true);
        markersChanged = true;
        if (!animatedPosRef.current[id]) {
          animatedPosRef.current[id] = { lat: driver.lat, lng: driver.lng, heading: null };
        }
      }
    });

    // Remove markers for drivers no longer present
    Object.keys(nativeMarkersRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        if (clustererRef.current) clustererRef.current.removeMarker(nativeMarkersRef.current[id], true);
        else nativeMarkersRef.current[id].setMap(null);
        delete nativeMarkersRef.current[id];
        delete driverDataRef.current[id];
        delete markerIconStateRef.current[id];
        delete animatedPosRef.current[id];
        delete activeAnimationsRef.current[id];
        markersChanged = true;
      }
    });
    // Render clusterer only when markers were added or removed AND we still have ≥1 marker
    if (markersChanged && clustererRef.current && Object.keys(nativeMarkersRef.current).length > 0) {
      try { clustererRef.current.render(); } catch (e) { /* empty algorithm input — ignore */ }
    }
  }, [data?.drivers, focusedDriver, buildMarkerIcon, mapReady]);

  // Update focused driver marker icon when focus changes — PERF: only update 2 markers (old + new)
  const prevFocusedRef = useRef(null);
  useEffect(() => {
    const oldId = prevFocusedRef.current;
    const newId = focusedDriver;
    prevFocusedRef.current = newId;
    // Update only the old focused marker (unfocus) and new focused marker (focus)
    [oldId, newId].forEach(id => {
      if (!id) return;
      const marker = nativeMarkersRef.current[id];
      const driver = driverDataRef.current[id];
      if (!marker || !driver) return;
      const isFocused = id === newId;
      const heading = animatedPosRef.current[id]?.heading;
      const iconKey = `${driver.calculated_status}_${driver.is_stale}_${isFocused}_${heading != null}`;
      marker.setIcon(buildMarkerIcon(driver, isFocused, heading));
      markerIconStateRef.current[id] = iconKey;
    });
  }, [focusedDriver, buildMarkerIcon]);

  // PERF: Native mission markers — avoid React re-renders inside GoogleMap
  const missionAnchorRef = useRef(null);
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google) return;
    if (!missionAnchorRef.current) missionAnchorRef.current = new window.google.maps.Point(12, 22);
    const missions = data?.missions?.all?.filter(m => m.pickup_lat && m.pickup_lng && m.status !== 'completed' && m.status !== 'cancelled') || [];
    const currentIds = new Set(missions.map(m => m.id));
    // Remove old markers
    Object.entries(missionMarkersRef.current).forEach(([id, marker]) => {
      if (!currentIds.has(id)) { marker.setMap(null); delete missionMarkersRef.current[id]; }
    });
    // Create or update mission markers
    missions.forEach(m => {
      const isUnassigned = m.status === 'unassigned' || m.status === 'proposed';
      if (missionMarkersRef.current[m.id]) {
        missionMarkersRef.current[m.id].setPosition({ lat: m.pickup_lat, lng: m.pickup_lng });
      } else {
        const icon = {
          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
          scale: 1.5, fillColor: isUnassigned ? '#F59E0B' : '#10B981',
          fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 1,
          anchor: missionAnchorRef.current,
        };
        const marker = new window.google.maps.Marker({
          position: { lat: m.pickup_lat, lng: m.pickup_lng },
          map: mapRef.current, icon, title: `${m.ref_id || 'Mission'} — ${m.pickup_address || ''}`,
          optimized: true,
        });
        marker.addListener('click', () => handleMissionSelect(m));
        missionMarkersRef.current[m.id] = marker;
      }
    });
  }, [data?.missions?.all, mapReady]);

  // Update InfoWindow position periodically when open — PERF: skip if position unchanged
  useEffect(() => {
    if (!infoWindowDriver) return;
    let lastLat = null, lastLng = null;
    const iv = setInterval(() => {
      const pos = animatedPosRef.current[infoWindowDriver];
      if (pos && (pos.lat !== lastLat || pos.lng !== lastLng)) {
        lastLat = pos.lat;
        lastLng = pos.lng;
        setInfoWindowPos({ lat: pos.lat, lng: pos.lng });
      }
    }, 500); // Reduced from 300ms to 500ms
    return () => clearInterval(iv);
  }, [infoWindowDriver]);

  // Cleanup all native markers and clusterer on unmount
  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        try {
          // Guard against MarkerClusterer assertion crash when markers array is empty
          // (SuperClusterAlgorithm.calculate fails on empty input during effect cleanup).
          if (Object.keys(nativeMarkersRef.current || {}).length > 0) {
            clustererRef.current.clearMarkers();
          }
        } catch (e) { /* ignore — unmount cleanup */ }
        clustererRef.current = null;
      }
      Object.values(nativeMarkersRef.current).forEach(m => m.setMap(null));
      nativeMarkersRef.current = {};
      if (globalAnimRef.current) cancelAnimationFrame(globalAnimRef.current);
    };
  }, []);

  // Debounce ref to prevent rapid simultaneous snapshot calls
  const fetchInProgressRef = useRef(false);
  const fetchSnapshot = useCallback(async (manual = false) => {
    // Prevent overlapping fetches
    if (fetchInProgressRef.current && !manual) return;
    fetchInProgressRef.current = true;
    if (manual) setRefreshing(true);
    const t0 = performance.now();
    try {
      const params = new URLSearchParams();
      if (filterParams.fleet) params.set('fleet', filterParams.fleet);
      if (filterParams.city) params.set('city', filterParams.city);
      if (missionDate) params.set('date', missionDate);
      if (missionDateEnd) params.set('date_end', missionDateEnd);
      if (missionType) params.set('mission_type', missionType);
      if (debugMode) params.set('debug', 'true');
      const { data: res } = await axios.get(`${API}/dispatch/pro/snapshot?${params}`, { headers: getHeaders() });
      perfRef.current.snapshotLatency = Math.round(performance.now() - t0);
      setData(res);
    } catch (err) { console.error('Snapshot error', err); }
    finally {
      setLoading(false);
      setRefreshing(false);
      fetchInProgressRef.current = false;
    }
  }, [filterParams, missionDate, missionDateEnd, missionType, debugMode]);

  // TL-30: Pause live updates when profile modal (history) is open
  const isHistoryMode = profileModal.open;

  // Bug 4: Auto-open driver profile from URL params (?driver=xxx&tab=history&date=YYYY-MM-DD)
  useEffect(() => {
    if (urlParamHandled || !data) return;
    const driverParam = searchParams.get('driver');
    const tabParam = searchParams.get('tab');
    const dateParam = searchParams.get('date');
    if (driverParam) {
      setProfileModal({ open: true, driverId: driverParam, initialTab: tabParam || 'history', initialDate: dateParam || null });
      setUrlParamHandled(true);
    }
  }, [data, searchParams, urlParamHandled]);

  useEffect(() => {
    if (isHistoryMode) return; // TL-30: Don't auto-refresh during history review
    fetchSnapshot();
    const iv = setInterval(fetchSnapshot, 30000); // 30s polling (WS covers real-time)
    return () => clearInterval(iv);
  }, [fetchSnapshot, isHistoryMode]);

  // Sync markers when snapshot brings new positions — uses animated pos as truth
  useEffect(() => {
    if (!data?.drivers) return;
    data.drivers.forEach(driver => {
      if (driver.lat == null || driver.lng == null) return;
      const id = driver.driver_id;
      const newPos = { lat: driver.lat, lng: driver.lng, heading: null };
      // Use animated position (visual truth) or last known position as reference
      const currentPos = animatedPosRef.current[id] || driverPositionsRef.current[id];
      // Skip identical position (no movement)
      if (currentPos && Math.abs(currentPos.lat - newPos.lat) < 0.000001 && Math.abs(currentPos.lng - newPos.lng) < 0.000001) return;
      if (currentPos) {
        animateMarker(id, currentPos, newPos);
      } else {
        // First time seeing this driver — set position immediately
        animatedPosRef.current[id] = newPos;
        driverPositionsRef.current[id] = newPos;
        const marker = nativeMarkersRef.current[id];
        if (marker) marker.setPosition({ lat: newPos.lat, lng: newPos.lng });
      }
    });
  }, [data?.drivers, animateMarker]);

  useEffect(() => {
    if (isHistoryMode) return; // TL-30: Don't connect WS during history review
    // Build WS URL from config.API_URL (supports split-domain architectures)
    const authToken = localStorage.getItem('access_token');
    let wsUrl;
    const apiUrl = config.API_URL; // '' (relative) or 'https://api.example.com'
    if (apiUrl && apiUrl.startsWith('http')) {
      // Absolute API URL → extract host for WS
      const parsed = new URL(apiUrl);
      const wsProto = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${wsProto}//${parsed.host}/api/dispatch/stream`;
    } else {
      // Relative API URL → same host as frontend
      const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${wsProto}//${window.location.host}/api/dispatch/stream`;
    }
    // Append auth token as query param
    if (authToken) wsUrl += `?token=${encodeURIComponent(authToken)}`;
    // PERF: Throttle location updates per driver (max 1 per second)
    const lastUpdateTimes = {};

    // Polling fallback state — activates if WS never opens or drops
    let pollingInterval = null;
    let wsOpened = false;
    const startPolling = () => {
      if (pollingInterval) return; // already polling
      pollingInterval = setInterval(() => { fetchSnapshot(); }, 10000);
    };
    const stopPolling = () => {
      if (pollingInterval) { clearInterval(pollingInterval); pollingInterval = null; }
    };

    try {
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          // T9: location_update → animate directly, no fetchSnapshot (avoids full reload)
          if (msg.type === 'location_update') {
            const { driver_id, lat, lng, heading, speed } = msg.data || {};
            if (lat == null || lng == null) return;
            // PERF: Throttle — max 1 update per driver per second
            const now = Date.now();
            if (lastUpdateTimes[driver_id] && now - lastUpdateTimes[driver_id] < 1000) return;
            lastUpdateTimes[driver_id] = now;
            const newPos = { lat, lng, heading: heading ?? null, speed: speed ?? null };
            // Use animated position (visual truth) as startPos
            const currentPos = animatedPosRef.current[driver_id] || driverPositionsRef.current[driver_id];
            // Skip duplicate position
            if (currentPos && Math.abs(currentPos.lat - lat) < 0.000001 && Math.abs(currentPos.lng - lng) < 0.000001) return;
            if (currentPos) {
              animateMarker(driver_id, currentPos, newPos);
            } else {
              animatedPosRef.current[driver_id] = newPos;
              driverPositionsRef.current[driver_id] = newPos;
              const marker = nativeMarkersRef.current[driver_id];
              if (marker) marker.setPosition({ lat, lng });
            }
          } else if (msg.type === 'mission_status_update') {
            // Real-time mission status sync — update driver state without full snapshot reload
            const { driver_id, new_status, status_label, mission_id } = msg.data || {};
            if (driver_id && new_status) {
              setData(prev => {
                if (!prev?.drivers) return prev;
                const updatedDrivers = prev.drivers.map(d => {
                  if (d.driver_id !== driver_id) return d;
                  const isMissionEnded = ['completed', 'no_show', 'cancelled'].includes(new_status);
                  return {
                    ...d,
                    active_mission_status: isMissionEnded ? null : new_status,
                    active_mission_ref: isMissionEnded ? null : (d.active_mission_ref || mission_id),
                    calculated_status: isMissionEnded ? (d.online ? 'EN_LIGNE' : 'DECONNECTE') : 'EN_MISSION',
                    has_active_mission: !isMissionEnded,
                  };
                });
                // Fix: prev.missions is an object {all, active, unassigned, ...}, not an array
                const patchList = (arr) => (arr || []).map(m =>
                  m.id !== mission_id ? m : { ...m, status: new_status, status_label: status_label || new_status }
                );
                const updatedMissions = prev.missions && typeof prev.missions === 'object' && !Array.isArray(prev.missions)
                  ? {
                      ...prev.missions,
                      all: patchList(prev.missions.all),
                      active: patchList(prev.missions.active),
                      unassigned: patchList(prev.missions.unassigned),
                      completed: patchList(prev.missions.completed),
                      late: patchList(prev.missions.late),
                      future: patchList(prev.missions.future),
                    }
                  : prev.missions;
                return { ...prev, drivers: updatedDrivers, missions: updatedMissions };
              });
              // For terminal statuses, do a full refresh to recategorize missions properly
              const terminalStatuses = ['completed', 'no_show', 'cancelled'];
              if (terminalStatuses.includes(new_status)) {
                setTimeout(() => fetchSnapshot(), 1000);
              }
            }
          } else if (msg.type === 'driver_vehicle_update') {
            // Real-time vehicle assignment sync
            const { driver_id, vehicle } = msg.data || {};
            if (driver_id) {
              setData(prev => {
                if (!prev?.drivers) return prev;
                const updatedDrivers = prev.drivers.map(d => {
                  if (d.driver_id !== driver_id) return d;
                  return {
                    ...d,
                    active_vehicle: vehicle ? {
                      id: vehicle.vehicle_id,
                      brand: vehicle.vehicle_name?.split(' ')[0] || '',
                      model: vehicle.vehicle_name?.split(' ').slice(1).join(' ') || '',
                      license_plate: vehicle.license_plate || '',
                      color: vehicle.color || '',
                      vehicle_type: vehicle.vehicle_category || '',
                    } : null,
                  };
                });
                return { ...prev, drivers: updatedDrivers };
              });
            }
          } else if (['mission_assigned', 'mission_unassigned', 'mission_completed', 'driver_status_change', 'mission_proposed', 'mission_retracted', 'assignment_update'].includes(msg.type)) {
            // Only refresh on critical state changes (includes assignment_update from assign/reassign/unassign)
            fetchSnapshot();
          }
          // Ignore heartbeat, pong, and other non-critical messages
        } catch { /* ignore parse errors */ }
      };
      ws.onopen = () => { wsOpened = true; stopPolling(); };
      ws.onerror = () => { if (!wsOpened) startPolling(); };
      ws.onclose = () => { if (!wsOpened) startPolling(); };
      wsRef.current = ws;

      // Safety: if WS never opens within 5 seconds, enable polling fallback
      setTimeout(() => { if (!wsOpened) startPolling(); }, 5000);
    } catch (e) {
      // WS constructor failure → immediate polling fallback
      startPolling();
    }
    return () => {
      stopPolling();
      wsRef.current?.close();
    };
  }, [fetchSnapshot, isHistoryMode, animateMarker]);

  // D2-11: Select driver from left list → focus map ONLY (no profile modal)
  const handleDriverSelect = useCallback((driver) => {
    setSelectedDriver(driver);
    setFocusedDriver(driver.driver_id);
    // D2-11: Only focus map, don't open profile
    if (mapRef.current && driver.lat != null) {
      mapRef.current.panTo({ lat: driver.lat, lng: driver.lng });
      mapRef.current.setZoom(16);
    }
    setInfoWindowDriver(driver.driver_id);
  }, []);

  // Mobile panel state
  const [mobilePanel, setMobilePanel] = useState('map'); // 'map' | 'drivers' | 'missions'
  const isMobileRef = useRef(typeof window !== 'undefined' && window.innerWidth < 768);

  // Track window resize for responsive
  useEffect(() => {
    const handleResize = () => { isMobileRef.current = window.innerWidth < 768; };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // D2-06: Click mission → open mission detail modal
  const [missionDetailModal, setMissionDetailModal] = useState({ open: false, mission: null });

  const handleMissionSelect = useCallback((mission) => {
    setMissionDetailModal({ open: true, mission });
  }, []);

  // D-30: Fetch compliance data + anomaly dashboard
  const fetchCompliance = useCallback(async () => {
    try {
      const [compRes, anomRes] = await Promise.all([
        axios.get(`${API}/dispatch/pro/compliance`, { headers: getHeaders() }),
        axios.get(`${API}/dispatch/pro/compliance-anomalies`, { headers: getHeaders() }).catch(() => ({ data: null })),
      ]);
      setComplianceData(compRes.data);
      setAnomalyDashboard(anomRes.data);
    } catch (err) { console.error('Compliance error', err); }
  }, []);

  // D-31: Fetch audit log
  const fetchAudit = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dispatch/pro/audit/assignments?per_page=50`, { headers: getHeaders() });
      setAuditData(data);
    } catch (err) { console.error('Audit error', err); }
  }, []);

  // Initial center: computed from first data load
  const initialCenterRef = useRef(null);
  if (!initialCenterRef.current && data?.drivers) {
    const driversWithPos = data.drivers.filter(d => d.lat != null);
    if (driversWithPos.length === 0) {
      initialCenterRef.current = { lat: 49.4944, lng: 0.1079 }; // Le Havre default
    } else if (driversWithPos.length === 1) {
      initialCenterRef.current = { lat: driversWithPos[0].lat, lng: driversWithPos[0].lng };
    } else {
      const sumLat = driversWithPos.reduce((s, d) => s + d.lat, 0);
      const sumLng = driversWithPos.reduce((s, d) => s + d.lng, 0);
      initialCenterRef.current = { lat: sumLat / driversWithPos.length, lng: sumLng / driversWithPos.length };
    }
  }
  const center = initialCenterRef.current || { lat: 49.4944, lng: 0.1079 };

  // Fit map bounds to show all drivers — only on initial load
  const initialFitDone = useRef(false);
  const fitMapToDrivers = useCallback((map) => {
    if (initialFitDone.current) return;
    const driversWithPos = data?.drivers?.filter(d => d.lat != null) || [];
    if (driversWithPos.length === 0 || !map) return;
    if (driversWithPos.length === 1) {
      map.panTo({ lat: driversWithPos[0].lat, lng: driversWithPos[0].lng });
      map.setZoom(14);
      initialFitDone.current = true;
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    driversWithPos.forEach(d => bounds.extend({ lat: d.lat, lng: d.lng }));
    map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
    initialFitDone.current = true;
  }, [data]);

  // Recenter map to fit all drivers with GPS positions (callable anytime)
  const recenterMap = useCallback(() => {
    const map = mapRef.current;
    if (!map || !data?.drivers) return;
    const driversWithPos = data.drivers.filter(d => d.lat != null);
    if (driversWithPos.length === 0) return;
    if (driversWithPos.length === 1) {
      map.panTo({ lat: driversWithPos[0].lat, lng: driversWithPos[0].lng });
      map.setZoom(14);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    driversWithPos.forEach(d => bounds.extend({ lat: d.lat, lng: d.lng }));
    map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
  }, [data]);

  // Auto-fit bounds once when data first loads
  useEffect(() => {
    if (!initialFitDone.current && mapReady && mapRef.current && data?.drivers?.filter(d => d.lat != null).length >= 1) {
      fitMapToDrivers(mapRef.current);
    }
  }, [data, fitMapToDrivers, mapReady]);

  if (!isLoaded || (loading && !data)) {
    return (
      <div className="h-screen bg-[#0a0a14] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
          <span className="text-sm text-white/40">Chargement du dispatch...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#0a0a14] text-white overflow-hidden" data-testid="partner-dispatch-pro-page">
      {/* Header — compact on mobile */}
      <header className="flex items-center justify-between px-2 md:px-4 py-1.5 md:py-2 bg-[#0e0e1a] border-b border-white/5 z-50 shrink-0">
        <div className="flex items-center gap-2 md:gap-3">
          <Link to="/partner/dashboard"><Button variant="ghost" size="sm" className="text-white/50 hover:text-white p-1 md:p-2"><ArrowLeft className="w-4 h-4" /></Button></Link>
          <div className="flex items-center gap-1.5 md:gap-2">
            <Radio className="w-3.5 md:w-4 h-3.5 md:h-4 text-[#C9A24D]" />
            <h1 className="font-semibold text-[#C9A24D] text-xs md:text-sm">Dispatch</h1>
            <span className="flex items-center gap-1 text-[9px] md:text-[10px] text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />LIVE
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setComplianceModal(true); fetchCompliance(); }}
            className="text-white/50 hover:text-white text-[9px] md:text-[10px] gap-1 hidden md:flex" data-testid="compliance-btn">
            <Shield className="w-3.5 h-3.5" />Conformité
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setAuditModal(true); fetchAudit(); }}
            className="text-white/50 hover:text-white text-[9px] md:text-[10px] gap-1 hidden md:flex" data-testid="audit-btn">
            <FileCheck className="w-3.5 h-3.5" />Audit
          </Button>
          {process.env.NODE_ENV !== 'production' && (
            <button onClick={() => setDebugMode(prev => !prev)}
              className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold transition-all hidden md:block ${debugMode
                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                : 'bg-white/5 text-white/30 border border-white/10 hover:text-white/50'}`}
              data-testid="debug-toggle-btn"
              title="Mode debug — affiche les métriques backend et frontend">
              DEBUG
            </button>
          )}
          <span className="text-[9px] md:text-[10px] text-white/30 hidden md:inline">{data?.snapshot_at ? new Date(data.snapshot_at).toLocaleTimeString('fr-FR') : ''}</span>
          <Button variant="ghost" size="sm" onClick={recenterMap}
            className="text-white/50 hover:text-white p-1 md:p-2" data-testid="recenter-map" title="Recentrer la carte">
            <MapPin className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => fetchSnapshot(true)} disabled={refreshing}
            className="text-white/50 hover:text-white p-1 md:p-2" data-testid="refresh-dispatch">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-[#C9A24D]' : ''}`} />
          </Button>
        </div>
      </header>

      {/* KPI Bar — hidden on mobile */}
      <div className="hidden md:block">{data?.kpi && <KPIBar kpi={data.kpi} />}</div>

      {/* KPI by Type/Subtype — workflow unification */}
      <div className="hidden md:block px-4 pt-1 pb-2 border-b border-white/5">
        <DispatchKpiByType date={missionDate} dateEnd={missionDateEnd || missionDate} getHeaders={getHeaders} />
      </div>

      {/* Main layout — responsive */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Left: Driver List — sidebar on desktop, bottom sheet on mobile */}
        <div className={`
          ${mobilePanel === 'drivers'
            ? 'fixed inset-x-0 bottom-0 z-40 h-[70vh] bg-[#0e0e1a] rounded-t-2xl shadow-2xl border-t border-white/10 animate-in slide-in-from-bottom duration-200'
            : 'hidden'}
          md:relative md:block md:w-72 md:h-auto md:rounded-none md:shadow-none md:border-r md:border-t-0 md:border-white/5 md:animate-none
          bg-[#0e0e1a] flex flex-col shrink-0
        `}>
          {/* Mobile drag handle */}
          <div className="md:hidden flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-white/20 rounded-full" />
          </div>
          <DriverList drivers={data?.drivers} selectedDriverId={selectedDriver?.driver_id}
            onSelectDriver={(d) => { handleDriverSelect(d); if (isMobileRef.current) setMobilePanel('map'); }}
            filters={data?.filters}
            onFilterChange={(f) => setFilterParams(prev => ({ ...prev, ...f }))} />
        </div>

        {/* Center: Map — always visible */}
        <div className="flex-1 relative">
          <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} defaultCenter={center} defaultZoom={13}
            options={{ styles: mapStyles, disableDefaultUI: true, zoomControl: true, mapTypeControl: false, gestureHandling: 'greedy' }}
            onLoad={map => {
              mapRef.current = map;
              // Initialize MarkerClusterer
              clustererRef.current = new MarkerClusterer({
                map,
                markers: [],
                renderer: {
                  render: ({ count, position }) => new window.google.maps.Marker({
                    position,
                    label: { text: String(count), color: '#fff', fontSize: '11px', fontWeight: 'bold' },
                    icon: {
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 18, fillColor: '#C9A24D', fillOpacity: 0.9,
                      strokeColor: '#fff', strokeWeight: 2,
                    },
                    zIndex: 1000 + count,
                  }),
                },
              });
              setMapReady(true);
              fitMapToDrivers(map);
              // Unified interaction detection — pauses all animations
              const startInteraction = () => {
                isInteractingRef.current = true;
                if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
              };
              const endInteraction = () => {
                if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
                interactionTimeoutRef.current = setTimeout(() => {
                  isInteractingRef.current = false;
                  // Flush pending position updates
                  const pending = pendingUpdatesRef.current.splice(0);
                  pending.forEach(({ driverId, endPos }) => {
                    const marker = nativeMarkersRef.current[driverId];
                    if (marker && endPos) marker.setPosition({ lat: endPos.lat, lng: endPos.lng });
                  });
                }, 300);
              };
              map.addListener('dragstart', startInteraction);
              map.addListener('zoom_changed', startInteraction);
              map.addListener('idle', endInteraction);
            }}>
            {/* Driver markers are native (managed via nativeMarkersRef) — only InfoWindow is React */}
            {infoWindowDriver && infoWindowPos && (() => {
              const driver = driverDataRef.current[infoWindowDriver] || data?.drivers?.find(d => d.driver_id === infoWindowDriver);
              if (!driver) return null;
              return (
                <InfoWindow position={infoWindowPos} onCloseClick={() => setInfoWindowDriver(null)}>
                  <div className="text-black min-w-[180px] md:min-w-[200px]">
                    <p className="font-semibold text-sm">{driver.full_name}</p>
                    <p className="text-xs text-gray-500">{DRIVER_STATUSES[driver.calculated_status]?.label}</p>
                    {driver.active_mission_status && (
                      <p className="text-xs font-semibold mt-0.5" style={{color: driver.active_mission_status === 'en_route' ? '#3B82F6' : driver.active_mission_status === 'waiting_for_pickup' ? '#F59E0B' : driver.active_mission_status === 'client_on_board' ? '#06B6D4' : driver.active_mission_status === 'no_show' ? '#DC2626' : '#8B5CF6'}}>
                        {MISSION_STATUS_LABELS[driver.active_mission_status] || driver.active_mission_status}
                        {driver.active_mission_ref && <span className="text-gray-400 font-normal"> · {driver.active_mission_ref}</span>}
                      </p>
                    )}
                    {driver.is_stale && <p className="text-xs text-red-600 font-medium mt-1">Position obsolète ({formatStaleDuration(driver.seconds_since_update)})</p>}
                    {driver.gps_connection && (
                      <p className={`text-xs font-medium mt-1 ${
                        driver.gps_connection === 'OK' ? 'text-green-600' :
                        driver.gps_connection === 'LAGGING' ? 'text-amber-600' :
                        driver.gps_connection === 'LOST' ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {driver.gps_connection === 'OK' ? 'GPS stable' :
                         driver.gps_connection === 'LAGGING' ? 'Connexion instable' :
                         driver.gps_connection === 'LOST' ? 'Signal GPS perdu' : 'GPS inconnu'}
                        {driver.seconds_since_heartbeat != null && ` (${driver.seconds_since_heartbeat}s)`}
                      </p>
                    )}
                    {driver.active_vehicle && <p className="text-xs text-gray-600 mt-1"><Car className="w-3 h-3 inline mr-1" />{driver.active_vehicle.brand} {driver.active_vehicle.model} · {driver.active_vehicle.license_plate}{driver.active_vehicle.color ? ` · ${driver.active_vehicle.color}` : ''}</p>}
                    {driver.fleet_name && <p className="text-xs text-gray-600">Flotte: {driver.fleet_name}</p>}
                    <p className="text-xs text-gray-500 mt-1">{driver.missions_today} mission(s) aujourd'hui</p>
                    <button onClick={() => setProfileModal({ open: true, driverId: driver.driver_id })}
                      className="mt-2 px-3 py-1 bg-[#C9A24D] text-white text-xs rounded hover:bg-[#b8913f] w-full">
                      Voir le profil complet
                    </button>
                  </div>
                </InfoWindow>
              );
            })()}
            {/* Mission markers are now native (see useEffect) */}
            {/* D2-22: Traffic Layer */}
            {showTraffic && <TrafficLayer />}
          </GoogleMap>

          {/* FT-7: Debug Panel — toggle or ?debug-map=true */}
          {debugEnabled && debugStats && (
            <div className="absolute bottom-16 md:bottom-4 right-14 md:right-16 z-[999] bg-black/85 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 font-mono text-[10px] leading-relaxed select-none max-h-[50vh] overflow-y-auto" data-testid="dispatch-debug-panel">
              <div className="text-[#C9A24D] font-semibold mb-1 text-[11px]">Debug Panel</div>
              <div className="text-white/40 text-[9px] mb-1">FRONTEND</div>
              <div className={debugStats.fps > 50 ? 'text-emerald-400' : debugStats.fps > 30 ? 'text-amber-400' : 'text-red-400'}>
                FPS: {debugStats.fps} <span className="text-white/30">({debugStats.fps > 50 ? 'OK' : debugStats.fps > 30 ? 'WARN' : 'LOW'})</span>
              </div>
              <div className={debugStats.frameTime < 20 ? 'text-emerald-400' : debugStats.frameTime < 40 ? 'text-amber-400' : 'text-red-400'}>
                Frame: {debugStats.frameTime}ms
              </div>
              <div className="text-white/70">Markers: {debugStats.markers}</div>
              <div className="text-white/70">Clusters: {clustererRef.current?.clusters?.length ?? '—'}</div>
              <div className={debugStats.animations > 50 ? 'text-red-400' : debugStats.animations > 0 ? 'text-amber-400' : 'text-white/70'}>
                Animations: {debugStats.animations} {debugStats.animations > 50 && <span className="text-red-400">OVERLOAD</span>}
              </div>
              <div className={debugStats.snapshotLatency > 500 ? 'text-red-400' : debugStats.snapshotLatency > 200 ? 'text-amber-400' : 'text-emerald-400'}>
                Snapshot: {debugStats.snapshotLatency}ms {debugStats.snapshotLatency > 500 && <span>SLOW</span>}
              </div>
              <div className="text-white/30 mt-0.5">CPU est: {debugStats.fps >= 55 ? '<5%' : `~${Math.round((1 - debugStats.fps / 60) * 100)}%`}</div>
              {debugStats.interacting && <div className="text-[#C9A24D] mt-0.5">PAUSED (interaction)</div>}

              {/* T9: Backend debug metrics */}
              {data?.debug && (
                <>
                  <div className="border-t border-white/10 mt-1.5 pt-1.5 text-white/40 text-[9px]">BACKEND</div>
                  <div className={data.debug.query_time_ms > 200 ? 'text-red-400' : data.debug.query_time_ms > 100 ? 'text-amber-400' : 'text-emerald-400'}>
                    Query: {data.debug.query_time_ms}ms
                  </div>
                  <div className="text-white/70">Drivers DB: {data.debug.total_drivers_fetched}</div>
                  <div className="text-white/70">Locations DB: {data.debug.total_locations_fetched}</div>
                  <div className="text-white/70">Active tasks: {data.debug.total_active_tasks}</div>
                  <div className="text-white/70">Active VTC: {data.debug.total_active_vtc}</div>
                  <div className="text-white/70">Active Exc: {data.debug.total_active_excursions}</div>
                  {data.debug.driver_details && (
                    <>
                      <div className="border-t border-white/10 mt-1.5 pt-1 text-white/40 text-[9px]">DRIVERS ({data.debug.driver_details.length})</div>
                      {data.debug.driver_details.map(d => (
                        <div key={d.id} className={`truncate max-w-[200px] ${d.is_stale ? 'text-red-400' : d.has_active_mission ? 'text-blue-400' : 'text-white/50'}`}>
                          {d.name?.split(' ')[0]}: {d.calculated_status} {d.seconds_since_update != null ? `${d.seconds_since_update}s` : ''}
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Traffic toggle — repositioned for mobile */}
          <button onClick={() => setShowTraffic(!showTraffic)}
            className={`absolute top-3 right-3 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[9px] md:text-[10px] font-medium backdrop-blur transition-all ${showTraffic
              ? 'bg-[#C9A24D]/20 text-[#C9A24D] border border-[#C9A24D]/30'
              : 'bg-[#0e0e1a]/80 text-white/50 border border-white/10 hover:text-white/70'}`}
            data-testid="traffic-toggle">
            {showTraffic ? 'Masquer trafic' : 'Trafic'}
          </button>

          {/* Legend — compact on mobile */}
          <div className="absolute bottom-16 md:bottom-4 left-2 md:left-4 bg-[#0e0e1a]/90 backdrop-blur border border-white/10 rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-[9px] md:text-[10px]">
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />En ligne</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Mission</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-500" />Hors ligne</span>
              <span className="flex items-center gap-1 hidden md:flex"><span className="w-2 h-2 rounded-full bg-red-500" />Obsolète</span>
              <span className="flex items-center gap-1"><span className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[7px] border-l-transparent border-r-transparent border-b-amber-400" />Libre</span>
              <span className="flex items-center gap-1"><span className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[7px] border-l-transparent border-r-transparent border-b-emerald-400" />Assignée</span>
            </div>
          </div>

          {/* MOBILE: Floating navigation bar */}
          <div className="md:hidden fixed bottom-2 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-[#0e0e1a]/95 backdrop-blur-lg border border-white/10 rounded-2xl p-1 shadow-2xl" data-testid="mobile-nav-bar">
            <button onClick={() => setMobilePanel(mobilePanel === 'drivers' ? 'map' : 'drivers')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${mobilePanel === 'drivers' ? 'bg-[#C9A24D] text-black' : 'text-white/60'}`}
              data-testid="mobile-nav-drivers">
              <Users className="w-4 h-4" />
              <span>{data?.drivers?.length || 0}</span>
            </button>
            <button onClick={() => setMobilePanel('map')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${mobilePanel === 'map' ? 'bg-[#C9A24D] text-black' : 'text-white/60'}`}
              data-testid="mobile-nav-map">
              <Navigation className="w-4 h-4" />
            </button>
            <button onClick={() => setMobilePanel(mobilePanel === 'missions' ? 'map' : 'missions')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${mobilePanel === 'missions' ? 'bg-[#C9A24D] text-black' : 'text-white/60'}`}
              data-testid="mobile-nav-missions">
              <MapPin className="w-4 h-4" />
              <span>{data?.missions?.all?.length || 0}</span>
            </button>
            <button onClick={() => { setComplianceModal(true); fetchCompliance(); }}
              className="flex items-center px-2.5 py-2 rounded-xl text-xs text-white/40"
              data-testid="mobile-nav-more">
              <Shield className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right: Mission List — sidebar on desktop, bottom sheet on mobile */}
        <div className={`
          ${mobilePanel === 'missions'
            ? 'fixed inset-x-0 bottom-0 z-40 h-[70vh] bg-[#0e0e1a] rounded-t-2xl shadow-2xl border-t border-white/10 animate-in slide-in-from-bottom duration-200'
            : 'hidden'}
          md:relative md:block md:w-80 md:h-auto md:rounded-none md:shadow-none md:border-l md:border-t-0 md:border-white/5 md:animate-none
          bg-[#0e0e1a] flex flex-col shrink-0
        `}>
          {/* Mobile drag handle */}
          <div className="md:hidden flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-white/20 rounded-full" />
          </div>
          <MissionList missions={data?.missions} onSelectMission={(m) => { handleMissionSelect(m); if (isMobileRef.current) setMobilePanel('map'); }}
            selectedDriverId={selectedDriver?.driver_id}
            currentDate={missionDate}
            currentDateEnd={missionDateEnd}
            onDateChange={(d) => setMissionDate(d)}
            onDateEndChange={(d) => setMissionDateEnd(d)}
            onTypeChange={(t) => setMissionType(t)} />
        </div>
      </div>

      {/* Mobile: Backdrop overlay when panels are open */}
      {mobilePanel !== 'map' && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setMobilePanel('map')} />
      )}

      {/* D-09/D-10/D-11: Driver Profile Modal */}
      <DriverProfileModal
        open={profileModal.open}
        onClose={() => { setProfileModal({ open: false, driverId: null }); setInfoWindowDriver(null); }}
        driverId={profileModal.driverId}
        initialTab={profileModal.initialTab}
        initialDate={profileModal.initialDate}
        unassignedMissions={data?.missions?.unassigned}
        fetchSnapshot={fetchSnapshot}
        onOpenMissionDetail={(m) => setMissionDetailModal({ open: true, mission: m })}
      />

      {/* D2-06: Mission Detail Modal */}
      <MissionDetailModal
        open={missionDetailModal.open}
        onClose={() => setMissionDetailModal({ open: false, mission: null })}
        mission={missionDetailModal.mission}
        fetchSnapshot={fetchSnapshot}
        drivers={data?.drivers}
      />

      {/* D-30: Compliance Dashboard Modal */}
      <Dialog open={complianceModal} onOpenChange={setComplianceModal}>
        <DialogContent className="bg-[#0e0e1a] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="compliance-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-[#C9A24D]" />Dashboard Conformité</DialogTitle>
          </DialogHeader>
          {complianceData ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="compliance-summary">
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                  <p className="text-xl font-bold text-red-400">{complianceData.summary.total_issues}</p>
                  <p className="text-[10px] text-red-400/70">Problèmes</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                  <p className="text-xl font-bold text-amber-400">{complianceData.summary.total_warnings}</p>
                  <p className="text-[10px] text-amber-400/70">Avertissements</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                  <p className="text-xl font-bold text-emerald-400">{complianceData.summary.total_issues === 0 && complianceData.summary.total_warnings === 0 ? '✓' : '—'}</p>
                  <p className="text-[10px] text-emerald-400/70">{complianceData.summary.total_issues === 0 ? 'Conforme' : 'Non conforme'}</p>
                </div>
              </div>

              {/* Expired docs */}
              {complianceData.expired_documents.length > 0 && (
                <div data-testid="compliance-expired-docs">
                  <h4 className="text-[10px] text-red-400 uppercase tracking-wider mb-1.5">Documents expirés ({complianceData.expired_documents.length})</h4>
                  {complianceData.expired_documents.map((d, i) => (
                    <div key={i} className="p-2 mb-1 rounded bg-red-500/5 border border-red-500/10 text-xs flex justify-between">
                      <span>{d.driver_name} — {d.document_type}</span>
                      <span className="text-red-400">{d.expiry_date}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Expiring docs */}
              {complianceData.expiring_documents.length > 0 && (
                <div data-testid="compliance-expiring-docs">
                  <h4 className="text-[10px] text-amber-400 uppercase tracking-wider mb-1.5">Documents expirant bientôt ({complianceData.expiring_documents.length})</h4>
                  {complianceData.expiring_documents.map((d, i) => (
                    <div key={i} className="p-2 mb-1 rounded bg-amber-500/5 border border-amber-500/10 text-xs flex justify-between">
                      <span>{d.driver_name} — {d.document_type}</span>
                      <span className="text-amber-400">{d.expiry_date}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Expired vehicles */}
              {complianceData.expired_vehicles.length > 0 && (
                <div data-testid="compliance-expired-vehicles">
                  <h4 className="text-[10px] text-red-400 uppercase tracking-wider mb-1.5">Véhicules expirés ({complianceData.expired_vehicles.length})</h4>
                  {complianceData.expired_vehicles.map((v, i) => (
                    <div key={i} className="p-2 mb-1 rounded bg-red-500/5 border border-red-500/10 text-xs">
                      <div className="flex justify-between"><span>{v.brand} {v.model} — {v.license_plate}</span><span className="text-red-400">{v.severity}</span></div>
                      <p className="text-[10px] text-red-400/70 mt-0.5">{v.issues.join(' · ')}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Expiring vehicles */}
              {complianceData.expiring_vehicles.length > 0 && (
                <div data-testid="compliance-expiring-vehicles">
                  <h4 className="text-[10px] text-amber-400 uppercase tracking-wider mb-1.5">Véhicules expirant bientôt ({complianceData.expiring_vehicles.length})</h4>
                  {complianceData.expiring_vehicles.map((v, i) => (
                    <div key={i} className="p-2 mb-1 rounded bg-amber-500/5 border border-amber-500/10 text-xs">
                      <div className="flex justify-between"><span>{v.brand} {v.model} — {v.license_plate}</span><span className="text-amber-400">{v.severity}</span></div>
                      <p className="text-[10px] text-amber-400/70 mt-0.5">{v.issues.join(' · ')}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Drivers without vehicles */}
              {complianceData.drivers_no_vehicle.length > 0 && (
                <div data-testid="compliance-no-vehicle">
                  <h4 className="text-[10px] text-red-400 uppercase tracking-wider mb-1.5">Chauffeurs sans véhicule approuvé ({complianceData.drivers_no_vehicle.length})</h4>
                  {complianceData.drivers_no_vehicle.map((d, i) => (
                    <div key={i} className="p-2 mb-1 rounded bg-red-500/5 border border-red-500/10 text-xs flex justify-between">
                      <span>{d.full_name}</span><span className="text-red-400/70">{d.issue}</span>
                    </div>
                  ))}
                </div>
              )}

              {complianceData.summary.total_issues === 0 && complianceData.summary.total_warnings === 0 && (
                <div className="text-center py-6">
                  <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-emerald-400">Tout est conforme</p>
                </div>
              )}

              {/* Anomaly Reliability Dashboard */}
              {anomalyDashboard && anomalyDashboard.drivers?.length > 0 && (
                <div data-testid="compliance-anomalies-dashboard">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-[10px] text-[#C9A24D] uppercase tracking-wider font-bold">
                      Score de fiabilité chauffeurs ({anomalyDashboard.date_from} → {anomalyDashboard.date_to})
                    </h4>
                    <div className="text-right">
                      <span className="text-xs text-white/40">Moyenne : </span>
                      <span className={`text-sm font-bold ${anomalyDashboard.avg_score >= 90 ? 'text-emerald-400' : anomalyDashboard.avg_score >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
                        {anomalyDashboard.avg_score}/100
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {anomalyDashboard.drivers.map((d, i) => {
                      const scoreColor = d.reliability_score >= 90 ? 'text-emerald-400 bg-emerald-500' : d.reliability_score >= 70 ? 'text-amber-400 bg-amber-500' : 'text-red-400 bg-red-500';
                      const barColor = d.reliability_score >= 90 ? 'bg-emerald-500' : d.reliability_score >= 70 ? 'bg-amber-500' : 'bg-red-500';
                      return (
                        <div key={i} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5" data-testid={`reliability-driver-${i}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-8 h-8 rounded-full ${barColor}/20 flex items-center justify-center text-xs font-bold ${scoreColor}`}>
                                {d.reliability_score}
                              </span>
                              <div>
                                <p className="text-xs text-white font-medium">{d.driver_name}</p>
                                <p className="text-[9px] text-white/30">{d.missions_count} mission(s) · {d.completed_count} terminée(s)</p>
                              </div>
                            </div>
                            {d.total_anomalies > 0 && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-medium">
                                {d.total_anomalies} anomalie(s)
                              </span>
                            )}
                          </div>
                          {/* Score bar */}
                          <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${d.reliability_score}%` }} />
                          </div>
                          {/* Detail row */}
                          {(d.unreported_stops > 0 || d.route_deviations > 0) && (
                            <div className="flex gap-3 mt-1.5 text-[9px]">
                              {d.unreported_stops > 0 && <span className="text-amber-400">{d.unreported_stops} arrêt(s) non signalé(s)</span>}
                              {d.route_deviations > 0 && <span className="text-red-400">{d.route_deviations} déviation(s)</span>}
                              <span className="text-white/20">{d.tracking_points} pts GPS</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C9A24D]" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* D-31: Audit Log Modal */}
      <Dialog open={auditModal} onOpenChange={setAuditModal}>
        <DialogContent className="bg-[#0e0e1a] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="audit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm"><FileCheck className="w-4 h-4 text-[#C9A24D]" />Journal d'affectation</DialogTitle>
          </DialogHeader>
          {auditData ? (
            <div className="space-y-1" data-testid="audit-log-list">
              <p className="text-[10px] text-white/30 mb-2">{auditData.total} entrée(s) au total</p>
              {auditData.logs.map(log => (
                <div key={log.id} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 text-xs" data-testid={`audit-entry-${log.id}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <Badge className={`text-[9px] border-0 ${log.action === 'ASSIGN' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {log.action}
                    </Badge>
                    <span className="text-[10px] text-white/30">{log.timestamp ? new Date(log.timestamp).toLocaleString('fr-FR') : ''}</span>
                  </div>
                  <p className="text-white/70">
                    {log.action === 'REASSIGN'
                      ? <>{log.performed_by} a réassigné à <span className="text-[#C9A24D]">{log.new_driver_name}</span></>
                      : <>{log.performed_by} a assigné <span className="text-[#C9A24D]">{log.new_driver_name}</span></>}
                  </p>
                  {log.reason && <p className="text-[10px] text-white/40 mt-0.5 italic">Raison: {log.reason}</p>}
                  <p className="text-[10px] text-white/20 mt-0.5">Mission: {log.mission_id}</p>
                </div>
              ))}
              {auditData.logs.length === 0 && <p className="text-xs text-white/30 text-center py-4">Aucune entrée</p>}
            </div>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C9A24D]" /></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
